import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { type Entity, type Finding, type TimelineEvent, type TimelineCategory } from "@/lib/intel/mock";
import { detectTargetType as sharedDetectTargetType, type DetectedTargetType } from "@/lib/intel/detect";
import { computeConfidenceModel, registrableDomain, SCORING_MODEL_VERSION, type ConfidenceBreakdown, type ScoreStatus } from "@/lib/intel/confidence";
import { estimateAmbiguityPrior, refineSignalCount } from "@/lib/intel/ambiguity";
import { assessAdverseSignals, ADVERSE_CAP, type AdverseAssessment } from "@/lib/intel/adverse";
import { buildSocialLane, firstUrl, laneForSource, type SocialProfile } from "@/lib/intel/lanes";

import {
  fetchOtxPassiveDns, fetchOtxReputation, fetchCertSpotterSubdomains,
  fetchUrlhaus, fetchPhishStats, fetchTechFingerprint, fetchSpfPosture,
  fetchFaviconHash, fetchHibpPastes, fetchHibpBreachedAccount, fetchOpenPgpKey, fetchGithubCommitsByEmail,
  fetchTorExitCheck, fetchReverseDns, fetchEtherscanLink,
  fetchOpenAlex, fetchCrossref, fetchGleif, fetchSecEdgar,
  fetchUkSanctions, fetchOfacSdn, fetchEuSanctions, fetchFbiWanted,
  fetchDorkPack, fetchGoogleNewsRss,
  fetchArxivDocs, fetchCourtListenerDocs, fetchGovInfoDocs,
  fetchInternetArchiveScholar, fetchWebPdfDocs, fetchWebOfficeDocs,
  type DocumentHit,
} from "@/lib/intel/collectors-extra";

import {
  fetchCrtShHistory, fetchDnssecStatus, fetchCaaRecords, fetchBimiRecord, fetchTlsChainInfo,
  fetchRipeStatNetwork, fetchRipeAbuseContact, fetchRipeRoutingHistory,
  fetchOrcidPeople, fetchViafAuthority, fetchPepScreen, fetchCrimeDesignations,
  fetchUsaSpending, fetchFederalRegister, fetchWikipediaProminence, fetchOpenLibraryAuthor,
  fetchSoftwareHeritage, fetchGithubOrg, fetchWikidataClaims,
  fetchLeakCheckPublic, fetchEmailMxPosture,
  fetchMempoolBtc, fetchBlockscoutEth, fetchPhoneCountry,
} from "@/lib/intel/collectors-more";

import { buildMeshContext, runSourceMesh } from "@/lib/intel/source-registry";
import { mineSelectors, runFamilies, type FamilyContext } from "@/lib/intel/families";

import { fuseCollectors, fusionDigest, type FusionResult } from "@/lib/intel/fusion";


const RefineSchema = z
  .object({
    dob: z.string().trim().max(40).optional().default(""),
    location: z.string().trim().max(120).optional().default(""),
    address: z.string().trim().max(200).optional().default(""),
    email: z.string().trim().max(200).optional().default(""),
    phone: z.string().trim().max(60).optional().default(""),
    employer: z.string().trim().max(160).optional().default(""),
    aliases: z.string().trim().max(200).optional().default(""),
    notes: z.string().trim().max(400).optional().default(""),
  })
  .partial()
  .default({});

export type ScanRefine = z.infer<typeof RefineSchema>;

const ScanInput = z.object({
  target: z.string().trim().min(3).max(180),
  refine: RefineSchema.optional(),
});

type TargetType = "email" | "domain" | "ip" | "wallet" | "phone" | "username" | "organization" | "person";

type CollectorRecord = {
  source: string;
  status: "ok" | "miss" | "error";
  summary: string;
  latencyMs: number;
  data?: JsonValue;
};

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

export type ScoreCategory = {
  key: string;
  label: string;
  points: number;
  cap: number;
  evidence: string[];
};

export type ScoreBreakdown = {
  base: number;
  riskScore: number;
  trustScore: number;
  /** Minimum risk enforced by conviction-grade adverse evidence. */
  floor?: number;
  categories: ScoreCategory[];
};


export type InvestigationStatus =
  | "SUCCESS"
  | "PARTIAL_RESULTS"
  | "NO_RESULTS"
  | "AMBIGUOUS"
  | "FAILED";

export type BackendScanResult = {
  id: string;
  target: string;
  targetType: TargetType;
  status: "completed" | "failed";
  /** Explicit outcome of the investigation — never inferred from HTTP 200. */
  investigationStatus: InvestigationStatus;
  /** Whether trust/risk was calculable from real evidence. */
  scoreStatus: ScoreStatus;
  scoringModelVersion: string;
  /** Evidence items that actually back the published scores. */
  usableEvidenceCount: number;
  /** Null whenever scoreStatus !== "CALCULATED". */
  riskScore: number | null;
  trustScore: number | null;
  scoreBreakdown: ScoreBreakdown;
  /** Null whenever confidenceBreakdown.status !== "CALCULATED". */
  confidence: number | null;
  confidenceBreakdown: ConfidenceBreakdown;
  sourceCount: number;

  findings: Finding[];
  entities: Entity[];
  timeline: TimelineEvent[];
  raw: {
    collectors: CollectorRecord[];
    socialLane?: SocialProfile[];
    sourceChain?: { source: string; status: string; latencyMs: number; url?: string; lane: string }[];
    legalScope: string;
    generatedAt: string;
  };
  createdAt: string;
  completedAt: string;
};

export const runIntelScan = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        target: z.string().trim().min(3).max(180),
        environment: z.enum(["sandbox", "live"]).default("live"),
        refine: RefineSchema.optional(),
        targetTypeOverride: z.enum(["person", "organization"]).optional(),
        workspace: z.string().trim().max(40).optional(),
        /** Result of the pre-scan disambiguation pass, when the analyst ran one. */
        ambiguity: z
          .object({
            score: z.number().min(0).max(100),
            candidateCount: z.number().min(0).max(20),
            selectedCandidate: z.string().trim().max(200).optional(),
          })
          .optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }): Promise<BackendScanResult> => {
    const { requireEntitlement } = await import("@/lib/payments/entitlements.server");
    await requireEntitlement({
      supabase: context.supabase,
      userId: context.userId,
      feature: "scan",
      environment: data.environment,
    });
    const target = data.target.trim();
    const detected = detectTargetType(target);
    const targetType: TargetType = data.targetTypeOverride ?? detected;
    const refine = normalizeRefine(data.refine);
    const refineTokens = refineTokenList(refine);
    const createdAt = new Date().toISOString();
    const db = supabaseAdmin as any;

    // Reap this analyst's stale "running" rows so a previously stuck scan can
    // never leave the vault (or the UI) waiting forever.
    try {
      await db
        .from("intel_scan_runs")
        .update({
          status: "failed",
          error: "Scan timed out and was closed automatically",
          completed_at: new Date().toISOString(),
        })
        .eq("user_id", context.userId)
        .eq("status", "running")
        .lt("created_at", new Date(Date.now() - 4 * 60_000).toISOString());
    } catch { /* never block a new scan on housekeeping */ }



      const { data: queued, error: insertError } = await db
      .from("intel_scan_runs")
      .insert({
        user_id: context.userId,
        target,
        target_type: targetType,
        status: "running",
        source_count: 0,
        workspace: data.workspace ?? null,
        raw: { stage: "queued", legalScope: "public-osint-only", refine },
      })
      .select("id, created_at")
      .single();

    if (insertError || !queued?.id) {
      throw new Error(`Backend scan queue failed: ${insertError?.message ?? "no scan id returned"}`);
    }

    // Global wall-clock budget. The whole run MUST return a result (even a
    // partial one) well before any edge/proxy timeout, otherwise the browser
    // sits on "Collecting…" forever and the row stays "running".
    const scanStartedAt = Date.now();
    const TOTAL_BUDGET_MS = 150_000;
    const remainingMs = () => Math.max(0, TOTAL_BUDGET_MS - (Date.now() - scanStartedAt));

    try {
      const collectors = await withDeadline(
        runPublicCollectors(target, targetType, refine),
        Math.min(90_000, Math.max(15_000, remainingMs() - 45_000)),
        () => [] as CollectorRecord[],
      );

      const filteredCollectors = applyRefineFilter(collectors, refineTokens, targetType);
      const refineSignals = refineSignalCount(refine);
      const ambiguityScore =
        data.ambiguity?.score ??
        estimateAmbiguityPrior({ target, targetType, refine }).score;
      const identityCandidateCount = data.ambiguity?.selectedCandidate
        ? 1
        : (data.ambiguity?.candidateCount ?? 0);
      const result = synthesizeScan({
        id: queued.id,
        target,
        targetType,
        createdAt: queued.created_at ?? createdAt,
        collectors: filteredCollectors,
        ambiguityScore,
        identityCandidateCount,
        refineSignals,
      });
      enrichWithHibp(result, filteredCollectors);
      const documents = extractDocumentsFromCollectors(filteredCollectors);

      // Evidence fusion network (L1-L4) — deterministic, explainable, and the
      // structured input the analyst model reasons over.
      const fusion: FusionResult = fuseCollectors(filteredCollectors);
      // Deterministic deep-pattern network over the full endpoint mesh.
      const { computeDeepPattern } = await import("@/lib/intel/deep-pattern");
      const deepPattern = computeDeepPattern(filteredCollectors);
      result.raw = { ...result.raw, documents, fusion, deepPattern } as typeof result.raw;

      // Grok intelligence layer (L5) — analyze + Live Search enrichment.
      let grokAnalysis: unknown = null;
      let grokError: string | null = null;
      try {
        const { analyzeWithGrok, summarizeCollectors } = await import("@/lib/intel/grok.server");
        const corpus = `${fusionDigest(fusion)}\n\n${identityDigest(filteredCollectors)}\n\n${deepPattern.digest}\n\nRAW COLLECTOR EVIDENCE:\n${summarizeCollectors(filteredCollectors)}`;
        const refineContext = refineTokens.length

          ? `\n\nNARROWING CRITERIA PROVIDED BY ANALYST (use to disambiguate from other people/entities with similar names):\n${formatRefineForPrompt(refine)}`
          : "";
        // Feed prior user-flagged bad findings for THIS target back into the prompt
        // so the analyst avoids repeating known-wrong claims / sources.
        let correctionsContext = "";
        try {
          const { data: corr } = await db
            .from("finding_corrections")
            .select("source,finding_title,reason")
            .eq("user_id", context.userId)
            .eq("target", target)
            .order("created_at", { ascending: false })
            .limit(15);
          if (Array.isArray(corr) && corr.length) {
            correctionsContext =
              "\n\nANALYST HAS PREVIOUSLY FLAGGED THESE AS INCORRECT — DO NOT REPEAT OR CITE:\n" +
              corr.map((c: any) => `- [${c.source}] ${c.finding_title}${c.reason ? ` (reason: ${c.reason})` : ""}`).join("\n");
          }
        } catch {}
        grokAnalysis = await withDeadline<unknown>(
          analyzeWithGrok({
            target,
            targetType,
            collectorCorpus: corpus + refineContext + correctionsContext,
          }),
          Math.min(70_000, Math.max(10_000, remainingMs() - 20_000)),
          () => null,
        );
        if (!grokAnalysis) grokError = "Analyst synthesis exceeded its time budget — collector evidence only.";
      } catch (e) {
        grokError = e instanceof Error ? e.message : "Grok analysis failed";
      }
      result.raw = { ...result.raw, grokAnalysis, grokError, refine } as typeof result.raw;

      // Second Grok pass: a sectioned deep dossier written from the pattern
      // network + first-pass analysis, so the UI can show detail per case
      // instead of one generic paragraph. Skipped when the budget is spent.
      let deepDossier: unknown = null;
      try {
        if (grokAnalysis && remainingMs() > 30_000) {
          const { writeDeepDossier } = await import("@/lib/intel/grok.server");
          deepDossier = await withDeadline<unknown>(
            writeDeepDossier({
              target,
              targetType,
              patternDigest: deepPattern.digest,
              analysis: grokAnalysis as Record<string, unknown>,
            }),
            Math.max(10_000, remainingMs() - 15_000),
            () => null,
          );
        }
      } catch {
        deepDossier = null;
      }

      result.raw = { ...result.raw, deepDossier } as typeof result.raw;

      // Re-score confidence now that we know the analytical citations and any
      // contradictions the analyst layer surfaced.
      {
        const analysis = grokAnalysis as
          | { citations?: unknown; conflicts?: unknown }
          | null;
        const okCollectors = filteredCollectors.filter((c) => c.status === "ok");
        const collectorUrls = collectUrlsFromCollectors(okCollectors);
        const collectorDomains = new Set(collectorUrls.map(registrableDomain).filter(Boolean));
        // A model-supplied citation may only *reference* evidence we actually
        // collected. It can never introduce a new "independent source".
        const grokCitations = (Array.isArray(analysis?.citations)
          ? (analysis!.citations as unknown[]).filter((u): u is string => typeof u === "string")
          : []
        ).filter((u) => collectorDomains.has(registrableDomain(u)));
        const conflictCount = Array.isArray(analysis?.conflicts) ? (analysis!.conflicts as unknown[]).length : 0;
        const citationUrls = [...collectorUrls, ...grokCitations];
        result.confidenceBreakdown = computeConfidenceModel({
          targetType,
          collectorsTotal: filteredCollectors.length,
          collectorsOk: okCollectors.length,
          collectorsError: filteredCollectors.filter((c) => c.status === "error").length,
          dataRichCollectors: okCollectors.filter((c) => c.data && Object.keys(c.data as object).length > 0).length,
          citationUrls,
          contradictionCount: conflictCount,
          ambiguityScore,
          identityCandidateCount,
          refineSignals,
          usableEvidenceCount: result.usableEvidenceCount,
        });
        result.confidence = result.confidenceBreakdown.overall;
        result.raw = { ...result.raw, confidenceBreakdown: result.confidenceBreakdown, ambiguity: data.ambiguity ?? null } as typeof result.raw;
      }

      // Structured observability — one line per completed investigation.
      console.log(
        JSON.stringify({
          event: "investigation.completed",
          investigationId: queued.id,
          status: result.investigationStatus,
          scoreStatus: result.scoreStatus,
          confidenceStatus: result.confidenceBreakdown.status,
          modelVersion: result.scoringModelVersion,
          sourceCount: result.sourceCount,
          successCount: result.confidenceBreakdown.coverage.successful,
          evidenceCount: result.usableEvidenceCount,
          confidence: result.confidence,
          riskScore: result.riskScore,
        }),
      );
      // INVARIANT 2 — a calculated score must be backed by evidence.
      if (result.confidenceBreakdown.status === "CALCULATED" && result.usableEvidenceCount <= 0) {
        console.error(
          JSON.stringify({ event: "invariant.violation", rule: "CONFIDENCE_WITHOUT_EVIDENCE", investigationId: queued.id }),
        );
      }

      const { error: updateError } = await db
        .from("intel_scan_runs")
        .update({
          status: "completed",
          risk_score: result.riskScore,
          confidence: result.confidence,
          score_status: result.scoreStatus,
          confidence_status: result.confidenceBreakdown.status,
          investigation_status: result.investigationStatus,
          scoring_model_version: result.scoringModelVersion,
          usable_evidence_count: result.usableEvidenceCount,
          source_count: result.sourceCount,
          findings: result.findings,
          entities: result.entities,
          timeline: result.timeline,
          raw: result.raw,
          completed_at: result.completedAt,
          required_clearance: computeRequiredClearance(result),
        })
        .eq("id", queued.id);


      if (updateError) {
        throw new Error(`Backend scan persistence failed: ${updateError.message}`);
      }

      // Fire-and-forget: post a notification for the owner.
      try {
        const clearance = computeRequiredClearance(result);
        const severity =
          result.riskScore === null ? "info" : result.riskScore >= 70 ? "critical" : result.riskScore >= 40 ? "warning" : "success";
        await db.from("notifications").insert({
          user_id: context.userId,
          type: "scan_complete",
          title: `Scan complete: ${target}`,
          body: `${result.findings.length} findings · risk ${result.riskScore}/100${clearance !== "L1" ? ` · ${clearance}` : ""}`,
          href: `/vault/${queued.id}`,
          severity,
          meta: { scanId: queued.id, riskScore: result.riskScore, clearance },
        });
      } catch { /* never break the scan */ }

      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown scan failure";
      await db
        .from("intel_scan_runs")
        .update({ status: "failed", error: message, completed_at: new Date().toISOString() })
        .eq("id", queued.id);
      try {
        await db.from("notifications").insert({
          user_id: context.userId,
          type: "scan_failed",
          title: `Scan failed: ${target}`,
          body: message.slice(0, 240),
          href: `/vault/${queued.id}`,
          severity: "critical",
          meta: { scanId: queued.id },
        });
      } catch { /* */ }
      throw new Error(`Backend scan failed: ${message}`);
    }
  });

function detectTargetType(target: string): TargetType {
  return sharedDetectTargetType(target) as TargetType;
}

function computeRequiredClearance(result: BackendScanResult): "L1" | "L2" | "L3" {
  const hibp = result.findings.find((f) => f.source === "HaveIBeenPwned breaches");
  if (!hibp) return "L1";
  const desc = `${hibp.title} ${hibp.description}`.toLowerCase();
  if (desc.includes("password")) return "L3";
  if (hibp.severity === "high" || hibp.severity === "critical") return "L2";
  return "L2";
}

function deriveHandles(name: string): string[] {
  const stripped = name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9 ]/g, " ").trim();
  const parts = stripped.split(/\s+/).filter(Boolean);
  if (!parts.length) return [];
  const first = parts[0]!;
  const last = parts[parts.length - 1] ?? "";
  const out = new Set<string>();
  out.add(parts.join(""));
  if (first && last && first !== last) {
    out.add(`${first}.${last}`);
    out.add(`${first}_${last}`);
    out.add(`${first}-${last}`);
    out.add(`${last}${first}`);
    out.add(`${last}.${first}`);
  }
  return [...out].filter((h) => h.length >= 3 && h.length <= 32);
}

function deriveEmailHandles(local: string): string[] {
  const base = local.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const out = new Set<string>();
  const cleaned = base.replace(/\+.*$/, ""); // strip +tag
  if (cleaned.length >= 3 && cleaned.length <= 32 && /^[a-z0-9._-]+$/.test(cleaned)) out.add(cleaned);
  const parts = cleaned.split(/[._\-]+/).filter((p) => p.length >= 2);
  if (parts.length >= 2) {
    const [a, b] = [parts[0]!, parts[parts.length - 1]!];
    for (const h of [`${a}${b}`, `${a}.${b}`, `${a}_${b}`, `${a}-${b}`, `${b}${a}`, `${b}.${a}`]) {
      if (h.length >= 3 && h.length <= 32) out.add(h);
    }
  }
  for (const p of parts) if (p.length >= 3 && p.length <= 32) out.add(p);
  return [...out];
}

function guessNameFromLocalPart(local: string): string | null {
  const cleaned = local.toLowerCase().replace(/\+.*$/, "").replace(/[0-9]+$/g, "");
  const parts = cleaned.split(/[._\-]+/).filter((p) => p.length >= 2 && /^[a-z]+$/.test(p));
  if (parts.length < 2) return null;
  return parts.map((p) => p[0]!.toUpperCase() + p.slice(1)).join(" ");
}

function normalizeForMatch(value: unknown): string {
  return recordSearchText(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function compactForMatch(value: unknown): string {
  return normalizeForMatch(value).replace(/\s+/g, "");
}

function personNameParts(target: string): string[] {
  return normalizeForMatch(target).split(/\s+/).filter((p) => p.length >= 2);
}

function recordSearchText(value: unknown): string {
  if (value == null) return "";
  if (["string", "number", "boolean"].includes(typeof value)) return String(value);
  try { return JSON.stringify(value); } catch { return String(value); }
}

function hasStrictPersonMatch(target: string, value: unknown): boolean {
  const parts = personNameParts(target);
  if (parts.length < 2) return normalizeForMatch(value).includes(parts[0] ?? "");
  const text = normalizeForMatch(value);
  const compact = text.replace(/\s+/g, "");
  const compactTarget = parts.join("");
  if (compact.includes(compactTarget)) return true;
  return parts.every((part) => new RegExp(`(^|\\s)${escapeRegExp(part)}($|\\s)`).test(text));
}

function hasStrongDerivedHandle(target: string, value: unknown): boolean {
  const text = compactForMatch(value);
  if (!text) return false;
  return deriveHandles(target).some((handle) => text.includes(compactForMatch(handle)));
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function strictPersonMiss(c: CollectorRecord, reason = "Filtered out: result did not contain the exact target name"): CollectorRecord {
  return { ...c, status: "miss", summary: reason };
}

function filterStrictItems(target: string, items: unknown[]): unknown[] {
  return items.filter((item) => hasStrictPersonMatch(target, item));
}

function enforcePersonRelevance(target: string, collectors: CollectorRecord[]): CollectorRecord[] {
  return collectors.map((collector) => strictPersonCollector(target, collector));
}

function strictPersonCollector(target: string, c: CollectorRecord): CollectorRecord {
  if (c.status !== "ok") return c;
  const source = c.source;
  const data = (c.data ?? {}) as Record<string, any>;

  if (source === "Investigator dork pack" || source === "Common Crawl") {
    return strictPersonMiss(c, "Search pivots are available, but no verified exact-name result was returned");
  }

  if (["Wikipedia", "DuckDuckGo Instant", "StackExchange", "Mastodon"].includes(source) && !hasStrictPersonMatch(target, c)) {
    return strictPersonMiss(c, "Result did not contain the exact target name");
  }

  if (["GitHub profile", "GitLab profile", "Bitbucket profile", "Keybase", "DEV.to profile", "Hacker News profile", "Reddit profile"].includes(source)) {
    const login = data.login ?? data.username ?? data.id ?? data.name ?? data.basics?.username;
    const profileText = `${c.summary} ${recordSearchText(c.data)}`;
    if (hasStrictPersonMatch(target, profileText) || hasStrongDerivedHandle(target, login)) return c;
    return strictPersonMiss(c, "Profile exists, but the public name/handle does not strictly match the target");
  }

  if (source === "Wikipedia search" && Array.isArray(data.results)) {
    const results = filterStrictItems(target, data.results);
    if (!results.length) return strictPersonMiss(c, "No exact-name Wikipedia match returned");
    return { ...c, summary: `${results.length} exact Wikipedia match${results.length === 1 ? "" : "es"} · top: ${(results[0] as any)?.title ?? "—"}`, data: sanitizeJson({ results }) };
  }

  if (source === "Wikidata" && Array.isArray(data.hits)) {
    const hits = filterStrictItems(target, data.hits);
    if (!hits.length) return strictPersonMiss(c, "No exact-name Wikidata entity returned");
    return { ...c, summary: `${hits.length} exact Wikidata entit${hits.length === 1 ? "y" : "ies"} · top: ${(hits[0] as any)?.label ?? "—"}`, data: sanitizeJson({ hits }) };
  }

  if (source === "OpenAlex authors" && Array.isArray(data.items)) {
    const items = filterStrictItems(target, data.items);
    if (!items.length) return strictPersonMiss(c, "No exact-name academic author match returned");
    return { ...c, summary: `${items.length} exact author match${items.length === 1 ? "" : "es"} · top: ${(items[0] as any)?.name ?? "—"}`, data: sanitizeJson({ total: items.length, items }) };
  }

  if (source === "OpenAlex (academic)" && Array.isArray(data.works)) {
    const works = filterStrictItems(target, data.works);
    if (!works.length) return strictPersonMiss(c, "No scholarly work contained the exact target name");
    return { ...c, summary: `${works.length} exact scholarly work${works.length === 1 ? "" : "s"} · top: ${(works[0] as any)?.title ?? "—"}`, data: sanitizeJson({ total: works.length, works }) };
  }

  if (source === "Crossref" && Array.isArray(data.items)) {
    const items = filterStrictItems(target, data.items);
    if (!items.length) return strictPersonMiss(c, "Crossref results did not contain the exact target name");
    return { ...c, summary: `${items.length} exact scholarly work${items.length === 1 ? "" : "s"}`, data: sanitizeJson({ items }) };
  }

  if (source === "GDELT news" && Array.isArray(data.articles)) {
    const articles = filterStrictItems(target, data.articles);
    if (!articles.length) return strictPersonMiss(c, "News results did not contain the exact target name");
    return { ...c, summary: `${articles.length} exact news article${articles.length === 1 ? "" : "s"} · top: ${(articles[0] as any)?.title?.slice(0, 80) ?? "—"}`, data: sanitizeJson({ articles }) };
  }

  if (source === "Google News RSS" && Array.isArray(data.items)) {
    const items = filterStrictItems(target, data.items);
    if (!items.length) return strictPersonMiss(c, "News RSS results did not contain the exact target name");
    return { ...c, summary: `${items.length} exact recent news article${items.length === 1 ? "" : "s"} · top: ${(items[0] as any)?.title?.slice(0, 80) ?? "—"}`, data: sanitizeJson({ items }) };
  }

  if (source === "Hacker News Algolia" && Array.isArray(data.hits)) {
    const hits = filterStrictItems(target, data.hits);
    if (!hits.length) return strictPersonMiss(c, "HN results did not contain the exact target name");
    return { ...c, summary: `${hits.length} exact HN mention${hits.length === 1 ? "" : "s"} · top: ${(hits[0] as any)?.title ?? "—"}`, data: sanitizeJson({ hits }) };
  }

  if (source === "Reddit search" && Array.isArray(data.items)) {
    const items = filterStrictItems(target, data.items);
    if (!items.length) return strictPersonMiss(c, "Reddit results did not contain the exact target name");
    return { ...c, summary: `${items.length} exact Reddit post${items.length === 1 ? "" : "s"} · top sub: ${(items[0] as any)?.subreddit ?? "—"}`, data: sanitizeJson({ items }) };
  }

  if ((source === "GitHub repositories" || source === "GitHub public search") && Array.isArray(data.items)) {
    const items = data.items.filter((item: unknown) => hasStrictPersonMatch(target, item) || hasStrongDerivedHandle(target, item));
    if (!items.length) return strictPersonMiss(c, "GitHub results did not strictly match the target name/handle");
    return { ...c, summary: `${items.length} strict GitHub match${items.length === 1 ? "" : "es"}`, data: sanitizeJson({ items }) };
  }

  if (["OpenSanctions screening", "OFAC SDN", "EU sanctions", "UK HMT sanctions", "FBI Wanted", "OpenCorporates", "GLEIF (LEI)", "SEC EDGAR"].includes(source) && !hasStrictPersonMatch(target, c)) {
    return strictPersonMiss(c, "Registry/watchlist results did not contain the exact target name");
  }

  return c;
}

function extractDomain(target: string, targetType: TargetType): string | null {
  if (targetType === "email") {
    const domain = target.split("@")[1]?.toLowerCase() ?? null;
    // Do NOT pivot to domain-wide collectors for free webmail providers — the
    // user is targeting a person/account, not gmail.com/outlook.com itself.
    if (domain && FREE_MAIL_DOMAINS.has(domain)) return null;
    return domain;
  }
  if (targetType === "domain") {
    try {
      const withProtocol = /^https?:\/\//i.test(target) ? target : `https://${target}`;
      return new URL(withProtocol).hostname.replace(/^www\./, "").toLowerCase();
    } catch {
      return target.replace(/^www\./, "").split("/")[0]?.toLowerCase() ?? null;
    }
  }
  return null;
}

const FREE_MAIL_DOMAINS = new Set<string>([
  "gmail.com", "googlemail.com", "outlook.com", "hotmail.com", "live.com", "msn.com",
  "yahoo.com", "yahoo.co.uk", "yahoo.fr", "yahoo.de", "ymail.com", "rocketmail.com",
  "icloud.com", "me.com", "mac.com",
  "proton.me", "protonmail.com", "pm.me",
  "aol.com", "gmx.com", "gmx.de", "gmx.net", "mail.com", "zoho.com",
  "yandex.com", "yandex.ru", "mail.ru", "inbox.ru", "list.ru", "bk.ru",
  "tutanota.com", "tuta.io", "fastmail.com", "hey.com", "qq.com", "163.com", "126.com",
  "naver.com", "daum.net", "hanmail.net", "seznam.cz", "web.de", "t-online.de",
  "orange.fr", "free.fr", "laposte.net", "sfr.fr", "wanadoo.fr",
  "libero.it", "virgilio.it", "alice.it", "tiscali.it",
  "bigpond.com", "optusnet.com.au", "rediffmail.com",
]);

// ---------- network helpers ----------
const DEFAULT_TIMEOUT_MS = 4500;

async function fetchWithTimeout(url: string, init: RequestInit & { timeout?: number } = {}): Promise<Response> {
  const { timeout = DEFAULT_TIMEOUT_MS, ...rest } = init;
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeout);
  try {
    return await fetch(url, { ...rest, signal: ctrl.signal, headers: { ...(rest.headers as any), ...UA } });
  } finally {
    clearTimeout(t);
  }
}

/**
 * Hard deadline wrapper. A scan must never hang because one upstream never
 * answers: every phase resolves with a fallback once its budget elapses.
 */
function withDeadline<T>(promise: Promise<T>, ms: number, fallback: () => T): Promise<T> {
  return new Promise<T>((resolve) => {
    let done = false;
    const t = setTimeout(() => {
      if (done) return;
      done = true;
      resolve(fallback());
    }, ms);
    promise.then(
      (v) => { if (!done) { done = true; clearTimeout(t); resolve(v); } },
      () => { if (!done) { done = true; clearTimeout(t); resolve(fallback()); } },
    );
  });
}



function normalizeRefine(input: ScanRefine | undefined): ScanRefine {
  const r = input ?? {};
  return {
    dob: (r.dob ?? "").trim(),
    location: (r.location ?? "").trim(),
    address: (r.address ?? "").trim(),
    email: (r.email ?? "").trim(),
    phone: (r.phone ?? "").trim(),
    employer: (r.employer ?? "").trim(),
    aliases: (r.aliases ?? "").trim(),
    notes: (r.notes ?? "").trim(),
  };
}

function refineTokenList(r: ScanRefine): string[] {
  const raw: string[] = [];
  if (r.location) raw.push(...r.location.split(/[,;]/));
  if (r.address) raw.push(...r.address.split(/[,;]/));
  if (r.employer) raw.push(r.employer);
  if (r.aliases) raw.push(...r.aliases.split(/[,;]/));
  if (r.email) raw.push(r.email);
  // NOTE: DOB and phone are intentionally NOT used as post-filter tokens —
  // text-based collectors (news, social, registries) almost never include them,
  // so requiring a match would discard legitimate results. They are still passed
  // to the AI analyst via formatRefineForPrompt() for disambiguation.
  return Array.from(
    new Set(
      raw
        .map((t) => normalizeForMatch(t))
        .map((t) => t.trim())
        .filter((t) => t.length >= 3),
    ),
  );
}

function formatRefineForPrompt(r: ScanRefine): string {
  const out: string[] = [];
  if (r.dob) out.push(`- DOB / birth year: ${r.dob}`);
  if (r.location) out.push(`- Current location: ${r.location}`);
  if (r.address) out.push(`- Address: ${r.address}`);
  if (r.employer) out.push(`- Employer / affiliation: ${r.employer}`);
  if (r.aliases) out.push(`- Known aliases / handles: ${r.aliases}`);
  if (r.email) out.push(`- Known email: ${r.email}`);
  if (r.phone) out.push(`- Known phone: ${r.phone}`);
  if (r.notes) out.push(`- Analyst notes: ${r.notes}`);
  return out.length ? out.join("\n") : "(none)";
}

// Sources whose results are name/text-based and therefore meaningfully filterable
// by narrowing criteria. We never demote infrastructure collectors (DNS, RDAP, etc).
const REFINE_FILTER_SOURCES = new Set<string>([
  "GDELT news", "Google News RSS", "Hacker News Algolia", "Reddit search",
  "Wikipedia", "Wikipedia search", "Wikidata", "DuckDuckGo Instant",
  "OpenAlex authors", "OpenAlex (academic)", "Crossref",
  "GitHub repositories", "GitHub public search", "GitHub profile",
  "GitLab profile", "Bitbucket profile", "Keybase", "DEV.to profile",
  "StackExchange", "Mastodon", "Hacker News profile", "Reddit profile",
  "OpenSanctions screening", "OFAC SDN", "EU sanctions", "UK HMT sanctions",
  "FBI Wanted", "OpenCorporates", "GLEIF (LEI)", "SEC EDGAR",
]);

function applyRefineFilter(
  collectors: CollectorRecord[],
  tokens: string[],
  targetType: TargetType,
): CollectorRecord[] {
  if (!tokens.length) return collectors;
  // Only narrow person/organization searches. For email/username the target itself
  // is precise — the primary collectors return profile pages for that exact handle,
  // which usually don't include the analyst's narrowing text (location/employer),
  // so post-filtering would strip legitimate results.
  if (!["person", "organization"].includes(targetType)) return collectors;
  return collectors.map((c) => {
    if (c.status !== "ok") return c;
    if (!REFINE_FILTER_SOURCES.has(c.source)) return c;
    const text = `${c.summary} ${recordSearchText(c.data)}`;
    const norm = normalizeForMatch(text);
    const matched = tokens.some((tok) => norm.includes(tok));
    if (matched) return c;
    return {
      ...c,
      status: "miss",
      summary: "Filtered out: result did not match the analyst's narrowing criteria",
    };
  });
}

async function runPublicCollectors(target: string, targetType: TargetType, refine?: ScanRefine): Promise<CollectorRecord[]> {
  const domain = extractDomain(target, targetType);
  const jobs: Promise<CollectorRecord>[] = [];
  const r = refine ?? {};

  if (domain) {
    jobs.push(fetchDnsRecord(domain, "A"));
    jobs.push(fetchDnsRecord(domain, "AAAA"));
    jobs.push(fetchDnsRecord(domain, "MX"));
    jobs.push(fetchDnsRecord(domain, "TXT"));
    jobs.push(fetchDnsRecord(domain, "NS"));
    jobs.push(fetchDnsRecord(domain, "SOA"));
    jobs.push(fetchDnsRecord(domain, "CAA"));
    jobs.push(fetchDnsRecord(domain, "SRV"));
    jobs.push(fetchDnsRecord(`_dmarc.${domain}`, "TXT", "DMARC policy"));
    jobs.push(fetchDnsRecord(`default._domainkey.${domain}`, "TXT", "DKIM (default)"));
    jobs.push(fetchDnsRecord(`_mta-sts.${domain}`, "TXT", "MTA-STS"));
    jobs.push(fetchRdap(`https://rdap.org/domain/${encodeURIComponent(domain)}`, "RDAP domain"));
    jobs.push(fetchCertificates(domain));
    jobs.push(fetchHttpHeaders(`https://${domain}`));
    jobs.push(fetchTextResource(`https://${domain}/robots.txt`, "robots.txt"));
    jobs.push(fetchTextResource(`https://${domain}/.well-known/security.txt`, "security.txt"));
    jobs.push(fetchTextResource(`https://${domain}/sitemap.xml`, "sitemap.xml"));
    jobs.push(fetchTextResource(`https://${domain}/humans.txt`, "humans.txt"));
    jobs.push(fetchTextResource(`https://${domain}/ads.txt`, "ads.txt"));
    jobs.push(fetchWaybackAvailability(domain));
    jobs.push(fetchWaybackCdxCount(domain));
    jobs.push(fetchUrlscan(domain));
    jobs.push(fetchMozillaObservatory(domain));
    jobs.push(fetchHackerTarget(domain, "hostsearch"));
    jobs.push(fetchHackerTarget(domain, "dnslookup"));
    jobs.push(fetchHackerTarget(domain, "reverseiplookup"));
    jobs.push(fetchHackerTarget(domain, "aslookup"));
    jobs.push(fetchHackerTarget(domain, "geoip"));
    jobs.push(fetchHackerTarget(domain, "httpheaders"));
    jobs.push(fetchGdelt(domain));
    jobs.push(fetchOpenCorporates(domain));
    jobs.push(fetchHackerNewsMentions(domain));
    jobs.push(fetchWikipediaSummary(domain));
    jobs.push(fetchRedditMentions(domain));
    jobs.push(fetchGithubCodeMentions(domain));
    jobs.push(fetchOtxPassiveDns(domain));
    jobs.push(fetchOtxReputation(domain));
    jobs.push(fetchCertSpotterSubdomains(domain));
    jobs.push(fetchUrlhaus(domain));
    jobs.push(fetchPhishStats(domain));
    jobs.push(fetchTechFingerprint(domain));
    jobs.push(fetchSpfPosture(domain));
    jobs.push(fetchFaviconHash(domain));
    jobs.push(fetchCrtShHistory(domain));
    jobs.push(fetchDnssecStatus(domain));
    jobs.push(fetchCaaRecords(domain));
    jobs.push(fetchBimiRecord(domain));
    jobs.push(fetchTlsChainInfo(domain));
  }

  if (targetType === "ip") {
    jobs.push(fetchRdap(`https://rdap.org/ip/${encodeURIComponent(target)}`, "RDAP IP"));
    jobs.push(fetchIpGeo(target));
    jobs.push(fetchIpWhoIs(target));
    jobs.push(fetchBgpIp(target));
    jobs.push(fetchShodanInternetDB(target));
    jobs.push(fetchGreyNoise(target));
    jobs.push(fetchHackerTarget(target, "reverseiplookup"));
    jobs.push(fetchHackerTarget(target, "geoip"));
    jobs.push(fetchHackerNewsMentions(target));
    jobs.push(fetchTorExitCheck(target));
    jobs.push(fetchReverseDns(target));
    jobs.push(fetchRipeStatNetwork(target));
    jobs.push(fetchRipeAbuseContact(target));
    jobs.push(fetchRipeRoutingHistory(target));
  }

  if (targetType === "username") {
    const handle = target.replace(/^@/, "");
    jobs.push(fetchGithubUserSearch(handle));
    jobs.push(fetchGithubUserProfile(handle));
    jobs.push(fetchGitlabUser(handle));
    jobs.push(fetchBitbucketUser(handle));
    jobs.push(fetchKeybaseUser(handle));
    jobs.push(fetchDevToUser(handle));
    jobs.push(fetchStackExchangeUser(handle));
    jobs.push(fetchMastodonSearch(handle));
    jobs.push(fetchHackerNewsUser(handle));
    jobs.push(fetchRedditUser(handle));
    jobs.push(...sherlockProbe(handle));
    jobs.push(fetchHackerNewsMentions(handle));
  }

  if (targetType === "email") {
    const local = target.split("@")[0] ?? "";
    const handleCandidates = deriveEmailHandles(local);
    const nameGuess = guessNameFromLocalPart(local);
    jobs.push(fetchGithubUserSearch(local));
    for (const h of handleCandidates.slice(0, 6)) {
      jobs.push(fetchGithubUserProfile(h));
      jobs.push(fetchGitlabUser(h));
      jobs.push(fetchKeybaseUser(h));
      jobs.push(fetchHackerNewsUser(h));
      jobs.push(fetchRedditUser(h));
      jobs.push(fetchDevToUser(h));
      jobs.push(fetchMastodonSearch(h));
    }
    jobs.push(fetchGravatar(target));
    jobs.push(fetchXposedOrNot(target));
    jobs.push(fetchDisposableEmail(target));
    jobs.push(...sherlockProbe(local).slice(0, 12));
    jobs.push(fetchHackerNewsMentions(target));
    jobs.push(fetchHackerNewsMentions(`"${local}"`));
    jobs.push(fetchHibpPastes(target));
    jobs.push(fetchHibpBreachedAccount(target));
    jobs.push(fetchOpenPgpKey(target));
    jobs.push(fetchGithubCommitsByEmail(target));
    jobs.push(fetchLeakCheckPublic(target));
    jobs.push(fetchEmailMxPosture(target));
    if (nameGuess) {
      const quoted = `"${nameGuess}"`;
      jobs.push(fetchGdelt(quoted));
      jobs.push(fetchGoogleNewsRss(quoted));
      jobs.push(fetchRedditMentions(quoted));
      jobs.push(fetchOpenAlexAuthors(nameGuess));
      jobs.push(fetchWikipediaSummary(nameGuess.replace(/\s+/g, "_")));
    }
  }

  if (targetType === "wallet") {
    if (/^0x[a-fA-F0-9]{40}$/.test(target)) {
      jobs.push(fetchEthplorerWallet(target));
      jobs.push(fetchEnsReverse(target));
      jobs.push(fetchBlockchairAddress("ethereum", target));
      jobs.push(fetchEtherscanLink(target));
    } else {
      jobs.push(fetchBlockstreamBtc(target));
      jobs.push(fetchBlockchairAddress("bitcoin", target));
      jobs.push(fetchMempoolBtc(target));
    }
    jobs.push(fetchHackerNewsMentions(target));
    jobs.push(fetchOfacSdn(target));
    jobs.push(fetchBlockscoutEth(target));
  }

  if (targetType === "phone") {
    jobs.push(fetchPhoneClassify(target));
    jobs.push(fetchHackerNewsMentions(target));
    jobs.push(fetchGdelt(target));
    jobs.push(fetchPhoneCountry(target));
  }

  if (targetType === "organization") {
    jobs.push(fetchWikipediaSummary(target));
    jobs.push(fetchOpenCorporates(target));
    jobs.push(fetchGdelt(target));
    jobs.push(fetchHackerNewsMentions(target));
    jobs.push(fetchRedditMentions(target));
    jobs.push(fetchGithubCodeMentions(target));
    jobs.push(fetchGithubOrg(target));
    jobs.push(fetchWikipediaProminence(target));
    jobs.push(fetchUsaSpending(target));
  }

  if (targetType === "person") {
    const exactTarget = `"${target}"`;
    jobs.push(fetchWikipediaSummary(target.replace(/\s+/g, "_")));
    jobs.push(fetchGdelt(exactTarget));
    jobs.push(fetchHackerNewsMentions(exactTarget));
    jobs.push(fetchRedditMentions(exactTarget));
    jobs.push(fetchGithubCodeMentions(exactTarget));
    jobs.push(fetchOpenAlexAuthors(target));
    // Refined news queries — issued as additional, separate calls so a strict
    // query that returns zero hits does not also wipe out the unrefined results.
    // Each narrow term gets its own simple `"Name" "Term"` query that providers
    // reliably accept (no OR/parens syntax that breaks Algolia/GDELT/RSS).
    if (r.location) jobs.push(fetchGdelt(`${exactTarget} "${r.location}"`));
    if (r.employer) jobs.push(fetchGdelt(`${exactTarget} "${r.employer}"`));
    if (r.location) jobs.push(fetchGoogleNewsRss(`${exactTarget} "${r.location}"`));
    if (r.employer) jobs.push(fetchGoogleNewsRss(`${exactTarget} "${r.employer}"`));
    const handles = deriveHandles(target).slice(0, 5);
    if (r.aliases) {
      r.aliases.split(/[,;\s]+/).map((a) => a.trim().replace(/^@/, "")).filter((a) => a.length >= 3 && a.length <= 32).slice(0, 4).forEach((a) => handles.push(a));
    }
    for (const h of handles) {
      jobs.push(fetchGithubUserProfile(h));
      jobs.push(fetchHackerNewsUser(h));
      jobs.push(fetchRedditUser(h));
      jobs.push(fetchKeybaseUser(h));
    }
    jobs.push(fetchGithubUserSearch(target));
    jobs.push(fetchOrcidPeople(target));
    jobs.push(fetchViafAuthority(target));
    jobs.push(fetchOpenLibraryAuthor(target));
    jobs.push(fetchWikipediaProminence(target));
  }

  // Universal collectors run on every target
  jobs.push(fetchDuckDuckGo(target));
  jobs.push(fetchWikidataSearch(target));
  jobs.push(fetchWikipediaOpenSearch(target));
  jobs.push(fetchCommonCrawlIndex(target));
  jobs.push(fetchOpenSanctionsScreen(target));
  jobs.push(fetchGdeltGeneric(target));
  jobs.push(fetchPivotLinks(target));
  jobs.push(fetchOpenAlex(target));
  jobs.push(fetchCrossref(target));
  jobs.push(fetchGleif(target));
  jobs.push(fetchSecEdgar(target));
  jobs.push(fetchUkSanctions(target));
  jobs.push(fetchOfacSdn(target));
  jobs.push(fetchEuSanctions(target));
  jobs.push(fetchFbiWanted(target));
  jobs.push(fetchDorkPack(target));
  jobs.push(fetchGoogleNewsRss(target));
  jobs.push(fetchPepScreen(target));
  jobs.push(fetchCrimeDesignations(target));
  jobs.push(fetchFederalRegister(target));
  jobs.push(fetchUsaSpending(target));
  jobs.push(fetchWikidataClaims(target));
  jobs.push(fetchSoftwareHeritage(target));

  // Document collectors — web-wide PDFs, filings, papers, court records.
  jobs.push(fetchArxivDocs(target));
  jobs.push(fetchCourtListenerDocs(target));
  jobs.push(fetchGovInfoDocs(target));
  jobs.push(fetchInternetArchiveScholar(target));
  jobs.push(fetchWebPdfDocs(target));
  jobs.push(fetchWebOfficeDocs(target));

  // Narrow-search email hint: if analyst provided an email in refine and the
  // primary target isn't itself an email, still run the HIBP breach lookup.
  if (targetType !== "email" && r.email && /@/.test(r.email)) {
    jobs.push(fetchHibpBreachedAccount(r.email));
    jobs.push(fetchHibpPastes(r.email));
  } else if (targetType !== "email") {
    jobs.push(Promise.resolve({
      source: "HaveIBeenPwned breaches",
      status: "miss" as const,
      summary: "Breach check not run — no email address in scope. Add an email in narrow search to check credential exposure.",
      latencyMs: 0,
      data: { checked: false, count: 0, breaches: [] },
    }));
  }

  // Run the legacy job list, the registry-driven source mesh and the
  // parameterized endpoint families together.
  const meshCtx = buildMeshContext({
    target,
    targetType,
    domain: domain ?? undefined,
    refineEmail: r.email,
  });
  const familyCtx: FamilyContext = {
    target,
    targetType,
    domains: domain ? [domain] : [],
    emails: [targetType === "email" ? target : "", r.email ?? ""].filter(Boolean),
    ips: targetType === "ip" ? [target] : [],
    wallets: targetType === "wallet" ? [target] : [],
    phones: [targetType === "phone" ? target : "", r.phone ?? ""].filter(Boolean),
    handles: targetType === "username" ? [target] : [],
    names: [target, ...(r.aliases ? r.aliases.split(/[,;]/).map((a) => a.trim()) : [])].filter(Boolean),
    locations: [r.location ?? "", r.address ?? ""].map((v) => v.trim()).filter(Boolean),
  };
  // Every phase is deadline-bounded: a single unresponsive upstream (crt.sh,
  // a registry, a search front) can otherwise stall the whole investigation.
  const emptyRun = { records: [], attempted: 0, hits: 0, executed: 0, wallMs: 0, perFamily: [], contactGraph: undefined };
  const [settled, mesh, families] = await Promise.all([
    Promise.allSettled(
      jobs.map((j) =>
        withDeadline(j, 22_000, () => ({
          source: "Collector",
          status: "error" as const,
          summary: "Collector timed out and was dropped from this scan",
          latencyMs: 22_000,
        })),
      ),
    ),
    withDeadline(runSourceMesh(meshCtx, { concurrency: 20, budgetMs: 26_000 }), 34_000, () => ({
      records: [] as CollectorRecord[],
      attempted: 0,
      completed: 0,
      timedOut: [] as string[],
      nodeIds: [] as string[],
      wallMs: 34_000,
    })),
    withDeadline(runFamilies(familyCtx, { budgetMs: 24_000 }), 34_000, () => emptyRun),

  ]);

  const collectors: CollectorRecord[] = settled.map((entry, index): CollectorRecord => {
    if (entry.status === "fulfilled") return entry.value;
    return {
      source: `Collector ${index + 1}`,
      status: "error",
      summary: entry.reason instanceof Error ? entry.reason.message : "Collector failed",
      latencyMs: 0,
    };
  });

  // Second round: pivot on selectors discovered in round one and re-probe the
  // families that apply to them. This is what turns a bare-name search into a
  // deep, corroborated dossier instead of a generic summary.
  // Source Fabric: registry-routed, provenance-carrying collection across the
  // 1,000+ declarative source definitions (only the relevant slice is queried).
  const fabricEntity =
    targetType === "organization" ? "organization" : targetType === "domain" ? "domain" : targetType === "person" ? "person" : "keyword";
  const fabricRun = await withDeadline(
    (async () => {
      const { runFabric, fabricToCollectorRecords } = await import("@/lib/intel/fabric/execute");
      const run = await runFabric(
        {
          target,
          entity: fabricEntity as "person" | "organization" | "domain" | "keyword",
          domain: domain ?? undefined,
          ip: targetType === "ip" ? target : undefined,
          email: targetType === "email" ? target : r.email,
          username: targetType === "username" ? target : undefined,
          country: (r.location ?? "").toLowerCase().includes("finland") || /\bfi\b/i.test(r.location ?? "") ? "FI" : undefined,
          // A Y-tunnus anywhere in the request unlocks the PRH business-id endpoints.
          ytunnus: /\b\d{7}-\d\b/.exec(`${target} ${r.location ?? ""} ${r.notes ?? ""}`)?.[0],
          budget: "DEEP",
        },
        { concurrency: 28, budgetMs: 22_000 },
      );
      return fabricToCollectorRecords(run);
    })(),
    28_000,
    () => [] as CollectorRecord[],
  );

  const seeds = mineSelectors([...collectors, ...mesh.records, ...families.records, ...fabricRun]);

  const pivotCtx: FamilyContext = {
    target,
    targetType,
    domains: seeds.domains.filter((d) => d !== domain).slice(0, 2),
    emails: seeds.emails.filter((e) => e !== r.email && e !== target).slice(0, 2),
    ips: seeds.ips.slice(0, 2),
    wallets: seeds.wallets.slice(0, 2),
    handles: seeds.handles.slice(0, 2),
    names: [],
  };
  const pivots =
    pivotCtx.domains?.length || pivotCtx.emails?.length || pivotCtx.ips?.length || pivotCtx.wallets?.length || pivotCtx.handles?.length
      ? await withDeadline(runFamilies(pivotCtx, { budgetMs: 12_000 }), 18_000, () => emptyRun)
      : null;

  const coverage: CollectorRecord = {
    source: "Endpoint family coverage",
    status: families.hits || pivots?.hits ? "ok" : "miss",
    summary: `${families.attempted + (pivots?.attempted ?? 0)} parameterized endpoints planned, ${
      families.executed + (pivots?.executed ?? 0)
    } executed, ${families.hits + (pivots?.hits ?? 0)} matches across ${families.perFamily.length} families`,
    latencyMs: families.wallMs + (pivots?.wallMs ?? 0),
    data: { round1: families.perFamily, round2: pivots?.perFamily ?? [], pivotSeeds: seeds },
  };

  // Merge, de-duplicating by source label (mesh wins only when it has data).
  const bySource = new Map<string, CollectorRecord>();
  for (const c of [...collectors, ...mesh.records, ...families.records, ...fabricRun, ...(pivots?.records ?? []), coverage]) {
    const existing = bySource.get(c.source);
    if (!existing || (existing.status !== "ok" && c.status === "ok")) bySource.set(c.source, c);
  }
  const merged = [...bySource.values()];
  return targetType === "person" ? enforcePersonRelevance(target, merged) : merged;
}



async function fetchDnsRecord(domain: string, type: string, label?: string): Promise<CollectorRecord> {
  const started = Date.now();
  const response = await fetchWithTimeout(`https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(domain)}&type=${type}`, {
    headers: { accept: "application/dns-json" },
    timeout: 6000,
  });
  if (!response.ok) throw new Error(`DNS ${type} lookup returned ${response.status}`);
  const data = await response.json() as { Answer?: Array<{ data?: string }> };
  const count = data.Answer?.length ?? 0;
  const source = label ?? `DNS ${type}`;
  return {
    source,
    status: count ? "ok" : "miss",
    summary: count ? `${count} public ${type} record${count === 1 ? "" : "s"} resolved` : `No public ${type} records returned`,
    latencyMs: Date.now() - started,
    data: sanitizeJson({ answers: data.Answer?.slice(0, 8) ?? [] }),
  };
}

async function fetchRdap(url: string, source: string): Promise<CollectorRecord> {
  const started = Date.now();
  const response = await fetchWithTimeout(url, { headers: { accept: "application/rdap+json, application/json" }, timeout: 8000 });
  if (!response.ok) {
    return { source, status: "miss", summary: `No RDAP record returned (${response.status})`, latencyMs: Date.now() - started };
  }
  const data = await response.json() as Record<string, unknown>;
  const handle = typeof data.handle === "string" ? data.handle : "record present";
  const name = typeof data.name === "string" ? data.name : undefined;
  return {
    source,
    status: "ok",
    summary: `${name ?? handle} resolved through public registration data`,
    latencyMs: Date.now() - started,
    data: sanitizeJson({ handle: data.handle, name: data.name, events: data.events, status: data.status }),
  };
}

async function fetchCertificates(domain: string): Promise<CollectorRecord> {
  const started = Date.now();
  const response = await fetchWithTimeout(`https://crt.sh/?q=${encodeURIComponent(`%.${domain}`)}&output=json`, { timeout: 12_000 });
  if (!response.ok) return { source: "Certificate Transparency", status: "miss", summary: `CT endpoint returned ${response.status}`, latencyMs: Date.now() - started };
  const text = await response.text();
  let rows: Array<{ name_value?: string }> = [];
  try { rows = JSON.parse(text) as Array<{ name_value?: string }>; } catch { rows = []; }
  const names = Array.from(new Set(rows.flatMap((r) => String(r.name_value ?? "").split("\n")).filter(Boolean))).slice(0, 12);
  return {
    source: "Certificate Transparency",
    status: names.length ? "ok" : "miss",
    summary: names.length ? `${names.length} certificate names/subdomains observed` : "No certificate transparency rows returned",
    latencyMs: Date.now() - started,
    data: sanitizeJson({ names }),
  };
}

async function fetchGithubUserSearch(username: string): Promise<CollectorRecord> {
  const started = Date.now();
  const response = await fetchWithTimeout(`https://api.github.com/search/users?q=${encodeURIComponent(username)}+in:login&type=Users&per_page=5`, {
    headers: { accept: "application/vnd.github+json", "user-agent": "CARIO-INTEL-OSINT" },
    timeout: 8000,
  });
  if (!response.ok) return { source: "GitHub public search", status: "miss", summary: `GitHub public search returned ${response.status}`, latencyMs: Date.now() - started };
  const data = await response.json() as { total_count?: number; items?: Array<{ login?: string; html_url?: string }> };
  return {
    source: "GitHub public search",
    status: (data.total_count ?? 0) > 0 ? "ok" : "miss",
    summary: `${data.total_count ?? 0} public GitHub account candidate${data.total_count === 1 ? "" : "s"}`,
    latencyMs: Date.now() - started,
    data: sanitizeJson({ items: data.items?.slice(0, 5) ?? [] }),
  };
}

function sanitizeJson(value: unknown): JsonValue {
  if (value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
  if (Array.isArray(value)) return value.map(sanitizeJson);
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, sanitizeJson(v)]),
    );
  }
  return String(value);
}

async function simulateCollector(source: string, summary: string): Promise<CollectorRecord> {
  const started = Date.now();
  await new Promise((resolve) => setTimeout(resolve, 35));
  return { source, status: "ok", summary, latencyMs: Date.now() - started };
}

const UA = { "user-agent": "CARIO-INTEL-OSINT/1.0 (+lawful public OSINT)" };

async function safeJson<T = any>(url: string, init?: RequestInit): Promise<{ ok: boolean; status: number; data?: T; text?: string }> {
  try {
    const r = await fetchWithTimeout(url, { ...init, timeout: 8000, headers: { ...(init?.headers as any), ...UA } });
    const text = await r.text();
    try { return { ok: r.ok, status: r.status, data: JSON.parse(text) as T, text }; }
    catch { return { ok: r.ok, status: r.status, text }; }
  } catch (e) {
    return { ok: false, status: 0, text: e instanceof Error ? e.message : "fetch failed" };
  }
}

// --- SSRF guard --------------------------------------------------------------
// Reject fetches to user-supplied hosts that resolve to private, loopback,
// link-local, CGNAT, or cloud-metadata addresses. Prevents an analyst from
// pointing a scan at a domain whose DNS points inside our own infrastructure.
function safeHostOrNull(url: string): string | null {
  try { return new URL(url).hostname.toLowerCase(); } catch { return null; }
}
function ipv4IsPrivate(ip: string): boolean {
  const parts = ip.split(".").map((n) => Number.parseInt(n, 10));
  if (parts.length !== 4 || parts.some((p) => !Number.isFinite(p) || p < 0 || p > 255)) return true;
  const [a, b] = parts;
  if (a === 10) return true;
  if (a === 127) return true;
  if (a === 0) return true;
  if (a === 169 && b === 254) return true;             // link-local + AWS/GCP/Azure metadata (169.254.169.254)
  if (a === 172 && b >= 16 && b <= 31) return true;    // RFC1918
  if (a === 192 && b === 168) return true;             // RFC1918
  if (a === 100 && b >= 64 && b <= 127) return true;   // CGNAT RFC6598
  if (a >= 224) return true;                            // multicast + reserved
  return false;
}
function ipv6IsPrivate(ip: string): boolean {
  const s = ip.toLowerCase();
  if (s === "::" || s === "::1") return true;
  if (s.startsWith("fe80:") || s.startsWith("fc") || s.startsWith("fd")) return true;
  if (s.startsWith("::ffff:")) {
    const v4 = s.slice(7);
    if (/^\d+\.\d+\.\d+\.\d+$/.test(v4)) return ipv4IsPrivate(v4);
  }
  return false;
}
const _hostSafeCache = new Map<string, { ok: boolean; at: number }>();
const HOST_CACHE_TTL_MS = 60_000;
async function isPublicHost(host: string): Promise<boolean> {
  if (!host) return false;
  const now = Date.now();
  const cached = _hostSafeCache.get(host);
  if (cached && now - cached.at < HOST_CACHE_TTL_MS) return cached.ok;
  // Reject IP literals in private ranges up-front.
  if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    const ok = !ipv4IsPrivate(host);
    _hostSafeCache.set(host, { ok, at: now });
    return ok;
  }
  if (host.includes(":")) {
    const ok = !ipv6IsPrivate(host);
    _hostSafeCache.set(host, { ok, at: now });
    return ok;
  }
  // localhost & bare hostnames aren't publicly routable → reject.
  if (host === "localhost" || !host.includes(".")) {
    _hostSafeCache.set(host, { ok: false, at: now });
    return false;
  }
  // Resolve via DNS-over-HTTPS and validate every returned address.
  try {
    const [a, aaaa] = await Promise.all([
      fetch(`https://dns.google/resolve?name=${encodeURIComponent(host)}&type=A`).then((r) => r.json() as Promise<{ Answer?: Array<{ data?: string; type?: number }> }>).catch(() => null),
      fetch(`https://dns.google/resolve?name=${encodeURIComponent(host)}&type=AAAA`).then((r) => r.json() as Promise<{ Answer?: Array<{ data?: string; type?: number }> }>).catch(() => null),
    ]);
    const v4s = (a?.Answer ?? []).filter((x) => x.type === 1).map((x) => x.data ?? "").filter(Boolean);
    const v6s = (aaaa?.Answer ?? []).filter((x) => x.type === 28).map((x) => x.data ?? "").filter(Boolean);
    if (v4s.length === 0 && v6s.length === 0) {
      _hostSafeCache.set(host, { ok: false, at: now });
      return false;
    }
    const anyPrivate =
      v4s.some(ipv4IsPrivate) || v6s.some(ipv6IsPrivate);
    const ok = !anyPrivate;
    _hostSafeCache.set(host, { ok, at: now });
    return ok;
  } catch {
    _hostSafeCache.set(host, { ok: false, at: now });
    return false;
  }
}

async function fetchHttpHeaders(url: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const host = safeHostOrNull(url);
    if (!host || !(await isPublicHost(host))) {
      return { source: "HTTP headers", status: "error", summary: "blocked: non-public target (SSRF guard)", latencyMs: Date.now() - started };
    }
    const r = await fetchWithTimeout(url, { method: "GET", redirect: "follow", headers: UA, timeout: 8000 });
    const headers: Record<string, string> = {};
    r.headers.forEach((v, k) => { headers[k] = v.slice(0, 400); });
    const tech: string[] = [];
    if (headers["server"]) tech.push(`server=${headers["server"]}`);
    if (headers["x-powered-by"]) tech.push(`powered=${headers["x-powered-by"]}`);
    if (headers["cf-ray"]) tech.push("Cloudflare");
    if (headers["x-vercel-id"]) tech.push("Vercel");
    if (headers["x-amz-cf-id"]) tech.push("AWS CloudFront");
    return {
      source: "HTTP headers",
      status: r.ok ? "ok" : "miss",
      summary: `HTTP ${r.status} · ${tech.length ? tech.join(", ") : "no fingerprint headers"}`,
      latencyMs: Date.now() - started,
      data: sanitizeJson({ status: r.status, headers, tech }),
    };
  } catch (e) {
    return { source: "HTTP headers", status: "error", summary: e instanceof Error ? e.message : "fetch failed", latencyMs: Date.now() - started };
  }
}

async function fetchTextResource(url: string, label: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const host = safeHostOrNull(url);
    if (!host || !(await isPublicHost(host))) {
      return { source: label, status: "error", summary: "blocked: non-public target (SSRF guard)", latencyMs: Date.now() - started };
    }
    const r = await fetchWithTimeout(url, { headers: UA, timeout: 8000 });
    if (!r.ok) return { source: label, status: "miss", summary: `${label} returned ${r.status}`, latencyMs: Date.now() - started };
    const text = (await r.text()).slice(0, 4000);
    const lines = text.split(/\r?\n/).filter(Boolean).length;
    return { source: label, status: "ok", summary: `${label} present · ${lines} lines · ${text.length} chars`, latencyMs: Date.now() - started, data: sanitizeJson({ preview: text.slice(0, 1200) }) };
  } catch (e) {
    return { source: label, status: "error", summary: e instanceof Error ? e.message : "fetch failed", latencyMs: Date.now() - started };
  }
}

async function fetchWaybackAvailability(target: string): Promise<CollectorRecord> {
  const started = Date.now();
  const r = await safeJson<{ archived_snapshots?: { closest?: { timestamp?: string; url?: string; available?: boolean } } }>(`https://archive.org/wayback/available?url=${encodeURIComponent(target)}`);
  const snap = r.data?.archived_snapshots?.closest;
  return {
    source: "Wayback Machine",
    status: snap?.available ? "ok" : "miss",
    summary: snap?.available ? `Archived snapshot ${snap.timestamp} · ${snap.url}` : "No archived snapshot found",
    latencyMs: Date.now() - started,
    data: sanitizeJson(snap ?? {}),
  };
}

async function fetchHackerNewsMentions(q: string): Promise<CollectorRecord> {
  const started = Date.now();
  const r = await safeJson<{ nbHits?: number; hits?: Array<{ title?: string; story_title?: string; url?: string; objectID?: string; created_at?: string }> }>(`https://hn.algolia.com/api/v1/search?query=${encodeURIComponent(q)}&hitsPerPage=8`);
  const hits = r.data?.hits ?? [];
  return {
    source: "Hacker News Algolia",
    status: (r.data?.nbHits ?? 0) > 0 ? "ok" : "miss",
    summary: `${r.data?.nbHits ?? 0} HN mentions · top: ${hits[0]?.title ?? hits[0]?.story_title ?? "—"}`,
    latencyMs: Date.now() - started,
    data: sanitizeJson({ hits: hits.slice(0, 8).map((h) => ({ title: h.title ?? h.story_title, url: h.url, id: h.objectID, ts: h.created_at })) }),
  };
}

async function fetchHackerNewsUser(username: string): Promise<CollectorRecord> {
  const started = Date.now();
  const r = await safeJson<{ id?: string; karma?: number; created?: number; about?: string }>(`https://hacker-news.firebaseio.com/v0/user/${encodeURIComponent(username)}.json`);
  if (!r.data?.id) return { source: "Hacker News profile", status: "miss", summary: `No HN profile for ${username}`, latencyMs: Date.now() - started };
  return { source: "Hacker News profile", status: "ok", summary: `HN user ${r.data.id} · karma ${r.data.karma ?? 0}`, latencyMs: Date.now() - started, data: sanitizeJson(r.data) };
}

async function fetchWikipediaSummary(q: string): Promise<CollectorRecord> {
  const started = Date.now();
  const r = await safeJson<{ title?: string; extract?: string; description?: string; content_urls?: any }>(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(q)}`);
  if (!r.ok || !r.data?.extract) return { source: "Wikipedia", status: "miss", summary: `No Wikipedia article for ${q}`, latencyMs: Date.now() - started };
  return { source: "Wikipedia", status: "ok", summary: `${r.data.title}: ${(r.data.extract ?? "").slice(0, 180)}`, latencyMs: Date.now() - started, data: sanitizeJson({ title: r.data.title, description: r.data.description, extract: r.data.extract, url: r.data.content_urls }) };
}

async function fetchWikidataSearch(q: string): Promise<CollectorRecord> {
  const started = Date.now();
  const r = await safeJson<{ search?: Array<{ id?: string; label?: string; description?: string; concepturi?: string }> }>(`https://www.wikidata.org/w/api.php?action=wbsearchentities&search=${encodeURIComponent(q)}&language=en&format=json&limit=5&origin=*`);
  const hits = r.data?.search ?? [];
  return { source: "Wikidata", status: hits.length ? "ok" : "miss", summary: hits.length ? `${hits.length} Wikidata entities · top: ${hits[0]?.label} (${hits[0]?.id})` : "No Wikidata entity match", latencyMs: Date.now() - started, data: sanitizeJson({ hits }) };
}

async function fetchWikipediaOpenSearch(q: string): Promise<CollectorRecord> {
  const started = Date.now();
  const r = await safeJson<[string, string[], string[], string[]]>(`https://en.wikipedia.org/w/api.php?action=opensearch&search=${encodeURIComponent(q)}&limit=6&namespace=0&format=json&origin=*`);
  const titles = r.data?.[1] ?? [];
  const descs = r.data?.[2] ?? [];
  const urls = r.data?.[3] ?? [];
  const results = titles.map((t, i) => ({ title: t, description: descs[i], url: urls[i] }));
  return {
    source: "Wikipedia search",
    status: titles.length ? "ok" : "miss",
    summary: titles.length ? `${titles.length} Wikipedia matches · top: ${titles[0]}` : "No Wikipedia article matches",
    latencyMs: Date.now() - started,
    data: sanitizeJson({ results }),
  };
}

async function fetchOpenAlexAuthors(q: string): Promise<CollectorRecord> {
  const started = Date.now();
  const r = await safeJson<{ meta?: { count?: number }; results?: any[] }>(`https://api.openalex.org/authors?search=${encodeURIComponent(q)}&per-page=5`);
  const total = r.data?.meta?.count ?? 0;
  const items = (r.data?.results ?? []).slice(0, 5).map((a: any) => ({
    name: a.display_name,
    affiliation: a.last_known_institution?.display_name,
    country: a.last_known_institution?.country_code,
    works: a.works_count,
    cited: a.cited_by_count,
    orcid: a.orcid,
    url: a.id,
  }));
  return {
    source: "OpenAlex authors",
    status: total > 0 ? "ok" : "miss",
    summary: total > 0 ? `${total} author matches · top: ${items[0]?.name ?? "—"}${items[0]?.affiliation ? " · " + items[0].affiliation : ""}` : "No academic author matches",
    latencyMs: Date.now() - started,
    data: sanitizeJson({ total, items }),
  };
}

async function fetchRedditMentions(q: string): Promise<CollectorRecord> {
  const started = Date.now();
  const r = await safeJson<{ data?: { children?: Array<{ data?: { title?: string; subreddit?: string; permalink?: string; created_utc?: number } }>; dist?: number } }>(`https://www.reddit.com/search.json?q=${encodeURIComponent(q)}&limit=8&sort=new`);
  const items = (r.data?.data?.children ?? []).map((c) => c.data).filter(Boolean) as any[];
  return { source: "Reddit search", status: items.length ? "ok" : "miss", summary: `${items.length} Reddit posts · top sub: ${items[0]?.subreddit ?? "—"}`, latencyMs: Date.now() - started, data: sanitizeJson({ items: items.slice(0, 8) }) };
}

async function fetchRedditUser(username: string): Promise<CollectorRecord> {
  const started = Date.now();
  const r = await safeJson<{ data?: { name?: string; total_karma?: number; created_utc?: number; subreddit?: any } }>(`https://www.reddit.com/user/${encodeURIComponent(username)}/about.json`);
  if (!r.ok || !r.data?.data?.name) return { source: "Reddit profile", status: "miss", summary: `No Reddit profile for ${username}`, latencyMs: Date.now() - started };
  return { source: "Reddit profile", status: "ok", summary: `u/${r.data.data.name} · karma ${r.data.data.total_karma ?? 0}`, latencyMs: Date.now() - started, data: sanitizeJson(r.data.data) };
}

async function fetchGithubCodeMentions(q: string): Promise<CollectorRecord> {
  const started = Date.now();
  const r = await safeJson<{ total_count?: number; items?: Array<{ full_name?: string; html_url?: string; description?: string; stargazers_count?: number }> }>(`https://api.github.com/search/repositories?q=${encodeURIComponent(q)}&per_page=5`, { headers: { accept: "application/vnd.github+json" } });
  const items = r.data?.items ?? [];
  return { source: "GitHub repositories", status: (r.data?.total_count ?? 0) > 0 ? "ok" : "miss", summary: `${r.data?.total_count ?? 0} repos mention target · top: ${items[0]?.full_name ?? "—"}`, latencyMs: Date.now() - started, data: sanitizeJson({ items: items.slice(0, 5) }) };
}

async function fetchGithubUserProfile(username: string): Promise<CollectorRecord> {
  const started = Date.now();
  const r = await safeJson<{ login?: string; name?: string; bio?: string; public_repos?: number; followers?: number; created_at?: string; html_url?: string; company?: string; blog?: string; location?: string }>(`https://api.github.com/users/${encodeURIComponent(username)}`, { headers: { accept: "application/vnd.github+json" } });
  if (!r.ok || !r.data?.login) return { source: "GitHub profile", status: "miss", summary: `No GitHub profile for ${username}`, latencyMs: Date.now() - started };
  return { source: "GitHub profile", status: "ok", summary: `${r.data.login} · ${r.data.public_repos ?? 0} repos · ${r.data.followers ?? 0} followers`, latencyMs: Date.now() - started, data: sanitizeJson(r.data) };
}

async function fetchGravatar(email: string): Promise<CollectorRecord> {
  const started = Date.now();
  // SHA256 hex of lowercased trimmed email per Gravatar spec
  const enc = new TextEncoder().encode(email.trim().toLowerCase());
  const buf = await crypto.subtle.digest("SHA-256", enc);
  const hex = Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
  const r = await safeJson<{ entry?: any[] }>(`https://en.gravatar.com/${hex}.json`);
  if (!r.ok || !r.data?.entry?.length) return { source: "Gravatar", status: "miss", summary: "No public Gravatar profile", latencyMs: Date.now() - started, data: sanitizeJson({ hash: hex }) };
  const entry = r.data.entry[0];
  return { source: "Gravatar", status: "ok", summary: `Gravatar profile found · ${entry?.preferredUsername ?? entry?.displayName ?? "anonymous"}`, latencyMs: Date.now() - started, data: sanitizeJson({ hash: hex, entry }) };
}

async function fetchIpGeo(ip: string): Promise<CollectorRecord> {
  const started = Date.now();
  const r = await safeJson<{ status?: string; country?: string; regionName?: string; city?: string; isp?: string; org?: string; as?: string; lat?: number; lon?: number }>(`http://ip-api.com/json/${encodeURIComponent(ip)}?fields=status,country,regionName,city,isp,org,as,lat,lon`);
  if (r.data?.status !== "success") return { source: "IP geolocation", status: "miss", summary: "No IP geolocation returned", latencyMs: Date.now() - started };
  return { source: "IP geolocation", status: "ok", summary: `${r.data.city ?? "?"} / ${r.data.country ?? "?"} · ${r.data.isp ?? r.data.org ?? "unknown ISP"}`, latencyMs: Date.now() - started, data: sanitizeJson(r.data) };
}

async function fetchBgpIp(ip: string): Promise<CollectorRecord> {
  const started = Date.now();
  const r = await safeJson<{ data?: { prefixes?: any[]; rir_allocation?: any } }>(`https://api.bgpview.io/ip/${encodeURIComponent(ip)}`);
  const prefixes = r.data?.data?.prefixes ?? [];
  return { source: "BGPView", status: prefixes.length ? "ok" : "miss", summary: prefixes.length ? `${prefixes.length} BGP prefix(es) announced` : "No BGP data", latencyMs: Date.now() - started, data: sanitizeJson({ prefixes: prefixes.slice(0, 5), rir: r.data?.data?.rir_allocation }) };
}

async function fetchEthplorerWallet(address: string): Promise<CollectorRecord> {
  const started = Date.now();
  const r = await safeJson<{ ETH?: { balance?: number; price?: any }; countTxs?: number; tokens?: any[] }>(`https://api.ethplorer.io/getAddressInfo/${encodeURIComponent(address)}?apiKey=freekey`);
  if (!r.ok || !r.data) return { source: "Ethplorer wallet", status: "miss", summary: "No wallet data returned", latencyMs: Date.now() - started };
  return { source: "Ethplorer wallet", status: "ok", summary: `ETH balance ${r.data.ETH?.balance ?? 0} · ${r.data.countTxs ?? 0} txs · ${r.data.tokens?.length ?? 0} tokens`, latencyMs: Date.now() - started, data: sanitizeJson({ eth: r.data.ETH, txs: r.data.countTxs, tokens: (r.data.tokens ?? []).slice(0, 8) }) };
}

async function fetchDuckDuckGo(q: string): Promise<CollectorRecord> {
  const started = Date.now();
  const r = await safeJson<{ AbstractText?: string; AbstractURL?: string; Heading?: string; RelatedTopics?: any[] }>(`https://api.duckduckgo.com/?q=${encodeURIComponent(q)}&format=json&no_html=1&skip_disambig=1`);
  const has = !!(r.data?.AbstractText || (r.data?.RelatedTopics?.length ?? 0) > 0);
  return { source: "DuckDuckGo Instant", status: has ? "ok" : "miss", summary: has ? `${r.data?.Heading || q}: ${(r.data?.AbstractText || "").slice(0, 160) || `${r.data?.RelatedTopics?.length ?? 0} related topics`}` : "No instant answer", latencyMs: Date.now() - started, data: sanitizeJson({ heading: r.data?.Heading, abstract: r.data?.AbstractText, url: r.data?.AbstractURL, related: (r.data?.RelatedTopics ?? []).slice(0, 6) }) };
}

async function fetchCommonCrawlIndex(q: string): Promise<CollectorRecord> {
  const started = Date.now();
  const r = await safeJson<any[]>(`https://index.commoncrawl.org/collinfo.json`);
  const latest = Array.isArray(r.data) ? r.data[0] : null;
  if (!latest?.["cdx-api"]) return { source: "Common Crawl", status: "miss", summary: "Common Crawl index list unavailable", latencyMs: Date.now() - started };
  const r2 = await safeJson<any>(`${latest["cdx-api"]}?url=${encodeURIComponent(q)}&limit=5&output=json`);
  const hits = (r2.text ?? "").split("\n").filter(Boolean).length;
  return { source: "Common Crawl", status: hits ? "ok" : "miss", summary: hits ? `${hits} archived crawls in ${latest.id}` : `No crawls in ${latest.id}`, latencyMs: Date.now() - started, data: sanitizeJson({ collection: latest.id, hits }) };
}

async function fetchOpenSanctionsScreen(q: string): Promise<CollectorRecord> {
  const started = Date.now();
  const r = await safeJson<{ total?: { value?: number }; results?: Array<{ caption?: string; schema?: string; datasets?: string[] }> }>(`https://api.opensanctions.org/search/default?q=${encodeURIComponent(q)}&limit=5`);
  const total = r.data?.total?.value ?? 0;
  const top = r.data?.results?.[0];
  return { source: "OpenSanctions screening", status: total > 0 ? "ok" : "miss", summary: total > 0 ? `${total} sanctions/PEP match(es) · top: ${top?.caption} (${top?.schema})` : "No sanctions/PEP/watchlist match", latencyMs: Date.now() - started, data: sanitizeJson({ total, results: (r.data?.results ?? []).slice(0, 5) }) };
}

function enrichWithHibp(result: BackendScanResult, collectors: CollectorRecord[]): void {
  const hibp = collectors.find((c) => c.source === "HaveIBeenPwned breaches");
  const pastes = collectors.find((c) => c.source === "HIBP pastes");
  void enrichWithHibpInner(result, hibp, pastes);
}

function extractDocumentsFromCollectors(collectors: CollectorRecord[]): DocumentHit[] {
  const out: DocumentHit[] = [];
  const seen = new Set<string>();
  const sourceRank: Record<string, number> = {
    "CourtListener": 0,
    "GovInfo": 1,
    "SEC EDGAR": 2,
    "arXiv": 3,
    "Archive Scholar": 4,
    "Web (PDF)": 5,
    "Web (Office)": 6,
  };
  for (const c of collectors) {
    if (c.status !== "ok") continue;
    const docs = (c.data as any)?.documents;
    if (!Array.isArray(docs)) continue;
    for (const d of docs as DocumentHit[]) {
      if (!d?.url || typeof d.url !== "string") continue;
      const key = d.url.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        title: (d.title ?? d.url).toString().slice(0, 240),
        url: d.url,
        snippet: d.snippet ? String(d.snippet).slice(0, 400) : undefined,
        published_at: d.published_at ? String(d.published_at) : undefined,
        mime: d.mime ? String(d.mime) : undefined,
        source: d.source ?? c.source,
      });
    }
  }
  out.sort((a, b) => {
    const ra = sourceRank[a.source] ?? 99;
    const rb = sourceRank[b.source] ?? 99;
    if (ra !== rb) return ra - rb;
    const da = a.published_at ?? "";
    const db = b.published_at ?? "";
    return db.localeCompare(da);
  });
  return out.slice(0, 100);
}

function enrichWithHibpInner(result: BackendScanResult, hibp: CollectorRecord | undefined, pastesRec?: CollectorRecord): void {
  const hd = (hibp?.data as any) ?? {};
  const breaches: any[] = (hd.breaches as any[]) ?? [];
  const pd = (pastesRec?.data as any) ?? {};
  const pasteCount: number = typeof pd.count === "number" ? pd.count : 0;
  const idxClean = result.findings.findIndex((f) => f.source === "HaveIBeenPwned breaches");
  if (!breaches.length) {
    // Still say something definitive: checked-and-clean, not-run, or errored.
    const checked = hd.checked === true || hibp?.status === "ok";
    const clean: Finding = {
      id: idxClean >= 0 ? result.findings[idxClean].id : `bf-hibp-${Date.now()}`,
      source: "HaveIBeenPwned breaches",
      category: "Credential Exposure",
      severity: "low",
      title: checked
        ? "No known credential exposure (HaveIBeenPwned)"
        : hibp?.status === "error"
        ? "Breach check incomplete (HaveIBeenPwned)"
        : "Breach check not run — no email in scope",
      description: checked
        ? `HaveIBeenPwned was queried for ${hd.email ?? "the subject email"} and returned no breached accounts.${pastesRec ? ` Paste corpus: ${pasteCount} public paste(s).` : ""}`
        : hibp?.status === "error"
        ? `${hibp.summary}. Treat credential exposure as unknown for this subject.`
        : "Provide an email address (narrow search) to run a HaveIBeenPwned credential-exposure check.",
      ts: new Date().toISOString().slice(0, 10),
      // 95 = the HIBP API itself returned a definitive "no breach" for this
      // address. 0 = the check never ran, so there is nothing to be confident in.
      confidence: checked ? 95 : 0,
    };
    if (idxClean >= 0) result.findings[idxClean] = clean; else result.findings.push(clean);
    if (!pasteCount) return;
  }
  const hasPasswords = breaches.some((b) => (b.dataClasses ?? []).some((c: string) => /password/i.test(c)));
  const anyVerifiedReal = breaches.some((b) => b.verified && !b.fabricated);
  const severity: Finding["severity"] = hasPasswords ? "critical" : anyVerifiedReal ? "high" : "medium";
  const topList = breaches.slice(0, 5).map((b) => `${b.title}${b.date ? ` (${b.date})` : ""}`).join("; ");
  const classes: string[] = Array.isArray(hd.dataClasses) ? hd.dataClasses : [];
  const detail = [
    breaches.length ? `Email appears in ${breaches.length} known breach(es).` : "",
    breaches.length ? `Top: ${topList}${breaches.length > 5 ? "…" : ""}.` : "",
    hd.verifiedCount ? `${hd.verifiedCount} verified by HIBP.` : "",
    hd.latestBreachDate ? `Most recent breach: ${hd.latestBreachDate}.` : "",
    typeof hd.accountsExposed === "number" && hd.accountsExposed > 0 ? `${hd.accountsExposed.toLocaleString()} accounts exposed in total across those incidents.` : "",
    classes.length ? `Exposed data types: ${classes.slice(0, 12).join(", ")}${classes.length > 12 ? "…" : ""}.` : "",
    pasteCount ? `Also found in ${pasteCount} public paste dump(s)${pd.latestPasteDate ? ` (latest ${String(pd.latestPasteDate).slice(0, 10)})` : ""}.` : "",
  ].filter(Boolean).join(" ");
  const idx = result.findings.findIndex((f) => f.source === "HaveIBeenPwned breaches");
  const aggregate: Finding = {
    id: idx >= 0 ? result.findings[idx].id : `bf-hibp-${Date.now()}`,
    source: "HaveIBeenPwned breaches",
    category: "Credential Exposure",
    severity: breaches.length ? severity : "medium",
    title: breaches.length
      ? `${breaches.length} breach exposure${breaches.length === 1 ? "" : "s"} via HaveIBeenPwned${hasPasswords ? " (passwords leaked)" : ""}`
      : `Email found in ${pasteCount} public paste dump${pasteCount === 1 ? "" : "s"} (HaveIBeenPwned)`,
    description: detail,
    ts: new Date().toISOString().slice(0, 10),
    confidence: 95,
  };
  if (idx >= 0) result.findings[idx] = aggregate; else result.findings.unshift(aggregate);
  const existingLabels = new Set(result.entities.map((e) => e.label.toLowerCase()));
  for (const b of breaches.slice(0, 20)) {
    const label = b.title || b.name;
    if (!label || existingLabels.has(String(label).toLowerCase())) continue;
    existingLabels.add(String(label).toLowerCase());
    result.entities.push({
      id: `hibp-${b.name || label}`.slice(0, 60),
      type: "org",
      label,
      sub: `HIBP breach${b.date ? ` · ${b.date}` : ""}${b.pwnCount ? ` · ${b.pwnCount.toLocaleString()} accounts` : ""}${b.dataClasses?.length ? ` · ${b.dataClasses.slice(0, 3).join(", ")}` : ""}`,
      confidence: b.verified ? 96 : 78,
      risk: hasPasswords ? 90 : b.sensitive ? 82 : 65,
    });
  }
  result.entities = result.entities.slice(0, 48);
  for (const b of breaches) {
    if (!b.date) continue;
    result.timeline.push({
      id: `hibp-t-${b.name}`.slice(0, 60),
      ts: `${b.date}T00:00:00.000Z`,
      title: `Breach: ${b.title || b.name}${b.pwnCount ? ` · ${b.pwnCount.toLocaleString()} accounts` : ""}`,
      source: "HaveIBeenPwned",
      severity: hasPasswords ? "high" : b.sensitive ? "high" : "medium",
      category: "breach",
      precision: "day",
      kind: "evidence",
    });
  }
  result.timeline.sort((a, b) => (a.ts < b.ts ? -1 : 1));

  // Recompute the breach category inside the same evidence-weighted model
  // instead of bumping the finished score, so the total stays explainable.
  const parts = breachScoreParts(breaches);
  const cap = 25;
  const raw = parts.reduce((s, p) => s + p.points, 0);
  const categories = result.scoreBreakdown.categories.map((c) =>
    c.key === "breach"
      ? { ...c, points: Math.round(Math.min(cap, raw) * 10) / 10, evidence: parts.map((p) => p.evidence) }
      : c,
  );
  if (!categories.some((c) => c.key === "breach")) {
    categories.push({ key: "breach", label: "Credential exposure", cap, points: Math.round(Math.min(cap, raw) * 10) / 10, evidence: parts.map((p) => p.evidence) });
  }
  const rescored = finalizeScore(categories, result.scoreBreakdown.floor ?? 0);
  result.scoreBreakdown = rescored;
  result.riskScore = rescored.riskScore;
  result.trustScore = rescored.trustScore;
}

function synthesizeScan(input: {
  id: string;
  target: string;
  targetType: TargetType;
  createdAt: string;
  collectors: CollectorRecord[];
  ambiguityScore: number;
  identityCandidateCount: number;
  refineSignals: number;
}): BackendScanResult {
  const { id, target, targetType, createdAt, collectors } = input;
  const domain = extractDomain(target, targetType);
  const okCollectors = collectors.filter((c) => c.status === "ok");
  const errorCount = collectors.filter((c) => c.status === "error").length;

  // Real wall-clock: derived from collector latencies, anchored to createdAt.
  const startedMs = Date.parse(createdAt) || Date.now();
  const maxLatency = collectors.reduce((m, c) => Math.max(m, c.latencyMs || 0), 0);
  const completedAt = new Date(startedMs + maxLatency).toISOString();

  const socialLane = buildSocialLane(okCollectors);
  const findings = [
    ...buildIdentityFindings(target, okCollectors, completedAt),
    ...buildFindings(target, okCollectors, completedAt),
  ];
  // Coverage floor — never show an empty dossier when the mesh actually hit.
  if (findings.length < 12) findings.push(...buildFallbackFindings(okCollectors, completedAt));
  const scoreBreakdown = computeRiskScore(okCollectors, target, targetType);
  const scoreBreakdownRisk = scoreBreakdown.riskScore;
  const adverse = scoreBreakdown.adverse;
  if (adverse && adverse.hits.length) {
    findings.unshift({
      id: `adverse-${id}`,
      category: "Adverse Media & Legal",
      severity: adverse.criminalRecord ? "critical" : adverse.worstTier === "B" ? "high" : "medium",
      title: adverse.criminalRecord
        ? `Criminal-record grade coverage found for ${target}`
        : `${adverse.hits.length} adverse mention(s) naming ${target}`,
      description: adverse.evidence.join(" · ").slice(0, 900),
      ts: completedAt.slice(0, 10),
      confidence: Math.min(95, 55 + new Set(adverse.hits.map((h) => h.domain || h.source)).size * 8),
    } as Finding);
  }

  // Usable evidence = collector payloads that actually carry structure, plus
  // distinct publisher domains actually cited. Nothing else may justify a score.
  const citationUrls = collectUrlsFromCollectors(okCollectors);
  const dataRich = okCollectors.filter((c) => c.data && Object.keys(c.data as object).length > 0).length;
  const usableEvidenceCount = countUsableEvidence(okCollectors, citationUrls);

  const confidenceBreakdown = computeConfidenceModel({
    targetType,
    collectorsTotal: collectors.length,
    collectorsOk: okCollectors.length,
    collectorsError: errorCount,
    dataRichCollectors: dataRich,
    citationUrls,
    contradictionCount: 0,
    ambiguityScore: input.ambiguityScore,
    identityCandidateCount: input.identityCandidateCount,
    refineSignals: input.refineSignals,
    documentsTotal: adverse?.totalDocs ?? 0,
    documentsNamingTarget: adverse?.namedDocs ?? 0,
    usableEvidenceCount,
  });
  const confidence = confidenceBreakdown.overall;

  // INVARIANT — a trust/risk score is a claim about the target. With no
  // evidence there is nothing to claim, so no number is published.
  const scoreStatus: ScoreStatus = usableEvidenceCount > 0 ? "CALCULATED" : "INSUFFICIENT_EVIDENCE";
  const riskScore = scoreStatus === "CALCULATED" ? scoreBreakdownRisk : null;
  const trustScore = scoreStatus === "CALCULATED" ? scoreBreakdown.trustScore : null;

  const investigationStatus: InvestigationStatus =
    usableEvidenceCount === 0
      ? "NO_RESULTS"
      : confidenceBreakdown.status !== "CALCULATED" && input.identityCandidateCount > 1
      ? "AMBIGUOUS"
      : errorCount > 0 && okCollectors.length / Math.max(1, collectors.length) < 0.6
      ? "PARTIAL_RESULTS"
      : "SUCCESS";

  const entities = buildEntities({ target, targetType, domain, riskScore: riskScore ?? 0, confidence: confidence ?? 0, okCollectors });
  const timeline = buildTimeline({ target, createdAt, completedAt, collectors, riskScore, confidence });


  return {
    id,
    target,
    targetType,
    status: "completed",
    investigationStatus,
    scoreStatus,
    scoringModelVersion: SCORING_MODEL_VERSION,
    usableEvidenceCount,
    riskScore,
    trustScore,
    scoreBreakdown,
    confidence,
    confidenceBreakdown,
    sourceCount: collectors.length,

    findings,
    entities,
    timeline,
    raw: {
      collectors,
      socialLane,
      // Per-scan provenance manifest: every source touched, with outcome and
      // timing, so the evidence chain is auditable and exportable.
      sourceChain: collectors.map((c) => ({
        source: c.source,
        status: c.status,
        latencyMs: c.latencyMs,
        url: firstUrl(c),
        lane: laneForSource(c.source),
      })),
      legalScope: "LEGAL OSINT ONLY: public DNS, RDAP, certificate transparency, public code/search indexes, sanctions/news/archive screening labels.",
      generatedAt: completedAt,
    },
    createdAt,
    completedAt,
  };
}

// ============================================================================
// Evidence-weighted scoring.
//
// Every point must be traceable to an actual adverse hit inside a collector
// payload — never to the fact that a collector merely ran. Each category has a
// hard cap and diminishing returns, so one noisy category cannot dominate and
// identical collector output always yields the identical score.
// ============================================================================

// No decorative baseline: risk starts at zero and only real adverse evidence
// moves it. A clean subject scores 0, not "3 because we ran a scan".
const SCORE_BASE = 0;

/** Diminishing-returns curve: first hit counts most, each extra hit less. */
function dim(count: number, unit: number, cap: number): number {
  if (count <= 0) return 0;
  return Math.min(cap, unit * (1 + Math.log2(count)));
}

function num(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function computeRiskScore(
  okCollectors: CollectorRecord[],
  target = "",
  targetType = "person",
): ScoreBreakdown & { adverse?: AdverseAssessment } {
  const bySource = new Map(okCollectors.map((c) => [c.source, c] as const));
  const get = (s: string) => bySource.get(s);
  const dataOf = (s: string) => (get(s)?.data as any) ?? null;
  let adverseSummary: AdverseAssessment | null = null;

  const categories: ScoreCategory[] = [];

  const add = (key: string, label: string, cap: number, parts: Array<{ points: number; evidence: string }>) => {
    const used = parts.filter((p) => p.points > 0);
    const raw = used.reduce((s, p) => s + p.points, 0);
    categories.push({
      key,
      label,
      points: Math.round(Math.min(cap, raw) * 10) / 10,
      cap,
      evidence: used.map((p) => p.evidence),
    });
  };

  // --- Sanctions, watchlists & wanted notices (dominant category) ----------
  {
    const parts: Array<{ points: number; evidence: string }> = [];
    const osTotal = num(dataOf("OpenSanctions screening")?.total);
    if (osTotal > 0) {
      parts.push({ points: 28 + dim(osTotal, 5, 12), evidence: `OpenSanctions: ${osTotal} sanctions/PEP match(es)` });
    }
    for (const [src, label, unit] of [
      ["OFAC SDN", "OFAC SDN", 20],
      ["EU sanctions", "EU consolidated list", 16],
      ["UK HMT sanctions", "UK HMT list", 16],
    ] as const) {
      const total = num(dataOf(src)?.total);
      if (total > 0) parts.push({ points: unit + dim(total, 3, 8), evidence: `${label}: ${total} match(es)` });
    }
    const fbi = dataOf("FBI Wanted")?.items;
    if (Array.isArray(fbi) && fbi.length) {
      parts.push({ points: 20 + dim(fbi.length, 4, 8), evidence: `FBI Wanted: ${fbi.length} notice(s)` });
    }
    add("sanctions", "Sanctions & watchlists", 55, parts);
  }

  // --- Malware / phishing infrastructure -----------------------------------
  {
    const parts: Array<{ points: number; evidence: string }> = [];
    const urlhaus = num(dataOf("URLhaus")?.count);
    if (urlhaus > 0) parts.push({ points: dim(urlhaus, 11, 22), evidence: `URLhaus: ${urlhaus} malicious URL(s) on this host` });
    const phish = dataOf("PhishStats")?.samples;
    if (Array.isArray(phish) && phish.length) {
      parts.push({ points: dim(phish.length, 9, 18), evidence: `PhishStats: ${phish.length} phishing record(s)` });
    }
    const pulses = num(dataOf("AlienVault OTX reputation")?.pulse_info?.count);
    if (pulses > 0) parts.push({ points: dim(pulses, 5, 12), evidence: `AlienVault OTX: ${pulses} threat pulse(s)` });
    if (dataOf("Tor exit list")?.isExit === true) {
      parts.push({ points: 8, evidence: "IP is an active Tor exit node" });
    }
    add("threat_infra", "Malware & phishing infrastructure", 30, parts);
  }

  // --- Credential / breach exposure ----------------------------------------
  add("breach", "Credential exposure", 25, breachScoreParts(dataOf("HaveIBeenPwned breaches")?.breaches));

  // --- Adverse media, criminal record & legal signals ----------------------
  {
    const adverse = assessAdverseSignals({ target, targetType, collectors: okCollectors });
    categories.push({
      key: "adverse_media",
      label: "Adverse media, criminal record & legal signals",
      cap: ADVERSE_CAP,
      points: adverse.points,
      evidence: adverse.evidence,
    });
    adverseSummary = adverse;
  }

  // --- Court & regulatory filings naming the target ------------------------
  {
    const parts: Array<{ points: number; evidence: string }> = [];
    const cl = dataOf("CourtListener");
    const clCount = num(cl?.total ?? (Array.isArray(cl?.items) ? cl.items.length : 0));
    if (clCount > 0) parts.push({ points: dim(clCount, 6, 12), evidence: `CourtListener: ${clCount} court record(s) naming the target` });
    const gov = dataOf("GovInfo");
    const govCount = num(gov?.total ?? (Array.isArray(gov?.items) ? gov.items.length : 0));
    if (govCount > 0) parts.push({ points: dim(govCount, 3, 6), evidence: `GovInfo: ${govCount} government publication(s)` });
    add("legal_records", "Court & regulatory filings", 16, parts);
  }


  // --- Infrastructure hygiene ----------------------------------------------
  {
    const parts: Array<{ points: number; evidence: string }> = [];
    const grade = String(dataOf("Mozilla Observatory")?.grade ?? "");
    if (/^[DF]/.test(grade)) parts.push({ points: grade.startsWith("F") ? 5 : 3, evidence: `Mozilla Observatory grade ${grade}` });
    const dnsblHits = num(dataOf("DNS blocklist")?.hits);
    if (dnsblHits > 0) parts.push({ points: dim(dnsblHits, 3, 5), evidence: `${dnsblHits} DNS blocklist hit(s)` });
    add("infrastructure", "Infrastructure hygiene", 8, parts);
  }

  // Conviction-grade evidence must dominate the headline number: no target with
  // a corroborated criminal record may present as trustworthy, regardless of how
  // clean the rest of its infrastructure looks.
  const floor =
    adverseSummary?.criminalRecord ? 72 : adverseSummary?.worstTier === "B" ? 48 : adverseSummary?.worstTier === "C" ? 22 : 0;
  const scored = finalizeScore(categories, floor);
  return adverseSummary ? { ...scored, adverse: adverseSummary } : scored;
}


function breachScoreParts(breachesRaw: unknown): Array<{ points: number; evidence: string }> {
  const breaches: any[] = Array.isArray(breachesRaw) ? breachesRaw : [];
  if (!breaches.length) return [];
  const hasPasswords = breaches.some((b) => (b.dataClasses ?? []).some((c: string) => /password/i.test(c)));
  const verified = breaches.some((b) => b.verified && !b.fabricated);
  const parts = [
    { points: 8 + dim(breaches.length, 3, 9), evidence: `HaveIBeenPwned: ${breaches.length} breach exposure(s)` },
  ];
  if (hasPasswords) parts.push({ points: 8, evidence: "Leaked data classes include passwords" });
  if (verified) parts.push({ points: 3, evidence: "At least one breach is HIBP-verified" });
  return parts;
}

function finalizeScore(categories: ScoreCategory[], floor = 0): ScoreBreakdown {
  const total = categories.reduce((s, c) => s + c.points, 0);
  const riskScore = clamp(Math.max(floor, Math.round(SCORE_BASE + total)), 0, 99);
  return { base: SCORE_BASE, riskScore, trustScore: 100 - riskScore, floor, categories };

}

// Pulls every http(s) URL a collector actually returned, so source-independence
// is measured against real publisher domains rather than collector count.
/**
 * Counts evidence items that can legitimately back a score: structured
 * collector payloads that carry an actual record, plus distinct cited
 * publisher domains. Collectors that merely ran contribute nothing.
 */
function countUsableEvidence(ok: CollectorRecord[], citationUrls: string[]): number {
  let n = 0;
  for (const c of ok) {
    const data = c.data as Record<string, unknown> | null | undefined;
    if (!data || typeof data !== "object") continue;
    const meaningful = Object.entries(data).some(([k, v]) => {
      if (k === "queries" || k === "categories") return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === "number") return v > 0;
      if (typeof v === "string") return v.trim().length > 0;
      if (v && typeof v === "object") return Object.keys(v).length > 0;
      return v === true;
    });
    if (meaningful) n++;
  }
  const domains = new Set(citationUrls.map(registrableDomain).filter(Boolean));
  return n + domains.size;
}

function collectUrlsFromCollectors(ok: CollectorRecord[], limit = 400): string[] {
  const urls = new Set<string>();
  for (const c of ok) {
    if (!c.data) continue;
    let text = "";
    try { text = JSON.stringify(c.data); } catch { continue; }
    for (const m of text.matchAll(/https?:\/\/[^\s"'\\<>()]{4,300}/g)) {
      urls.add(m[0]);
      if (urls.size >= limit) return [...urls];
    }
  }
  return [...urls];
}

// Only emit findings from ok collectors, dedup by (source,title), and skip
// generic no-hit / error rows so the ledger reflects real evidence.
function buildFindings(target: string, ok: CollectorRecord[], completedAt: string): Finding[] {
  const seen = new Map<string, Finding>();
  let i = 0;
  for (const c of ok) {
    const title = (c.summary || c.source).slice(0, 200);
    const key = `${c.source}::${title.toLowerCase()}`;
    if (seen.has(key)) continue;
    const severity = severityForCollector(c);
    const conf = confidenceForCollector(c);
    const url = firstUrl(c);
    seen.set(key, {
      id: `bf-${++i}`,
      source: c.source,
      category: categoryForCollector(c.source),
      severity,
      title,
      description: `Public source ${c.source} returned data for ${target}. Latency ${c.latencyMs}ms.`,
      ts: completedAt.slice(0, 10),
      confidence: conf,
      url,
      lane: laneForSource(c.source),
      retrievedAt: completedAt,
      latencyMs: c.latencyMs,
    });
  }
  return [...seen.values()];
}

/**
 * Social profiles and corroborated contact facts are the two things a person
 * search is actually judged on, so they become first-class findings instead of
 * living inside one collector payload nobody opens.
 */
function buildIdentityFindings(target: string, ok: CollectorRecord[], completedAt: string): Finding[] {
  const out: Finding[] = [];
  const day = completedAt.slice(0, 10);

  for (const [n, p] of buildSocialLane(ok).entries()) {
    out.push({
      id: `social-${n}`,
      source: p.sources[0] ?? "Social mesh",
      category: "Social Profile",
      severity: "low",
      title: `${p.platform}: ${p.handle ?? p.url}`,
      description: `${p.verdict === "confirmed" ? "Confirmed" : "Likely"} public profile — corroborated by ${p.corroboration} probe(s) across ${p.sources.length} source(s): ${p.sources.slice(0, 4).join(", ")}.`,
      ts: day,
      confidence: p.verdict === "confirmed" ? Math.min(95, 62 + p.corroboration * 6) : 55,
      url: p.url,
      lane: "social",
      retrievedAt: completedAt,
      corroboration: p.corroboration,
    });
  }

  const cg = ok.find((c) => c.source === "Contact graph")?.data as any;
  if (cg) {
    const emit = (
      items: { value: string; corroboration?: number; sources?: string[] }[] | undefined,
      kind: string,
      prefix: string,
    ) => {
      for (const [n, it] of (items ?? []).slice(0, 25).entries()) {
        const corr = it.corroboration ?? 1;
        out.push({
          id: `${prefix}-${n}`,
          source: (it.sources ?? [])[0] ?? "Contact mesh",
          category: kind,
          severity: "low",
          title: `${kind}: ${it.value}`,
          description: `Harvested from public pages naming ${target}; corroborated by ${corr} independent host(s)${it.sources?.length ? `: ${it.sources.slice(0, 4).join(", ")}` : ""}.`,
          ts: day,
          confidence: Math.min(92, 45 + corr * 12),
          lane: "contacts",
          retrievedAt: completedAt,
          corroboration: corr,
        });
      }
    };
    emit(cg.emails, "Email address", "cg-email");
    emit(cg.phones, "Phone number", "cg-phone");
    emit(cg.addresses, "Postal address", "cg-addr");
    emit(cg.relatives, "Associated person", "cg-rel");
  }

  return out;
}

/**
 * Coverage floor: when the collector layer surfaced almost nothing but the
 * probe mesh clearly hit, promote those raw hits rather than showing an empty
 * dossier. Confidence is derived from real corroboration — how many distinct
 * collectors independently returned the same publisher — never a flat guess.
 */
function buildFallbackFindings(ok: CollectorRecord[], completedAt: string): Finding[] {
  const out: Finding[] = [];
  const day = completedAt.slice(0, 10);
  // Count how many distinct collectors returned each publisher domain.
  const domainWitnesses = new Map<string, Set<string>>();
  for (const c of ok) {
    const hits = (c.data as any)?.hits;
    if (!Array.isArray(hits)) continue;
    for (const h of hits) {
      if (typeof h?.url !== "string") continue;
      const d = registrableDomain(h.url);
      if (!d) continue;
      if (!domainWitnesses.has(d)) domainWitnesses.set(d, new Set());
      domainWitnesses.get(d)!.add(c.source);
    }
  }
  let n = 0;
  for (const c of ok) {
    const hits = (c.data as any)?.hits;
    if (!Array.isArray(hits)) continue;
    for (const h of hits.slice(0, 8)) {
      if (typeof h?.url !== "string" || !/^https?:\/\//.test(h.url)) continue;
      const witnesses = domainWitnesses.get(registrableDomain(h.url))?.size ?? 1;
      out.push({
        id: `fb-${++n}`,
        source: `${c.source}${h.label ? ` · ${h.label}` : ""}`,
        category: "Probe match",
        severity: "low",
        title: h.detail ? String(h.detail).slice(0, 160) : `Match at ${h.label ?? h.url}`,
        description:
          witnesses > 1
            ? `Endpoint match seen by ${witnesses} independent collectors; still unverified against the subject — confirm before relying on it.`
            : "Uncorroborated single-collector endpoint match; confirm before relying on it.",
        ts: day,
        // 20 for a lone hit, rising only with genuine cross-collector agreement.
        confidence: clamp(20 + (witnesses - 1) * 12, 20, 70),
        url: h.url,
        lane: laneForSource(`${c.source} ${h.label ?? ""}`),
        retrievedAt: completedAt,
      });
      if (out.length >= 80) return out;
    }
  }
  return out;
}

function severityForCollector(c: CollectorRecord): Finding["severity"] {
  const s = c.source;
  if (["OFAC SDN", "EU sanctions", "UK HMT sanctions", "FBI Wanted"].includes(s)) return "critical";
  if (s === "OpenSanctions screening" && ((c.data as any)?.total ?? 0) > 0) return "critical";
  if (["URLhaus", "PhishStats"].includes(s)) return "high";
  if (s === "Tor exit list") return "medium";
  if (s === "HaveIBeenPwned breaches") return "high";
  return "low";
}

/**
 * Confidence in a collector-derived finding is a function of how much the
 * collector actually returned. A collector that answered with nothing gets 0 —
 * "we ran it" is not evidence.
 */
function confidenceForCollector(c: CollectorRecord): number {
  const data = (c.data as any) ?? null;
  if (!data || typeof data !== "object") return 0;
  const keys = Object.keys(data).length;
  if (keys === 0) return 0;
  return clamp(40 + Math.min(50, keys * 6), 40, 92);
}

function categoryForCollector(source: string): string {
  if (/sanction|OFAC|Wanted|OpenSanctions|HMT/i.test(source)) return "Sanctions & Watchlists";
  if (/HaveIBeenPwned|breach/i.test(source)) return "Credential Exposure";
  if (/URLhaus|Phish|OTX|Tor/i.test(source)) return "Threat Reputation";
  if (/GitHub|GitLab|Bitbucket|Reddit|Mastodon|Hacker News|DEV\.to|Keybase/i.test(source)) return "Public Profile";
  if (/News|GDELT|Wikipedia|Wikidata/i.test(source)) return "News & Reference";
  if (/EDGAR|OpenCorporates|GLEIF|Crossref|OpenAlex|arXiv|CourtListener|GovInfo/i.test(source)) return "Primary Documents";
  if (/DNS|Wayback|Common Crawl|CT|Certificate|WHOIS|RDAP|BGP|Geoloc/i.test(source)) return "Infrastructure";
  return "Public OSINT";
}

// Only promote entities backed by actual data — no per-collector artifacts.
function buildEntities(a: {
  target: string; targetType: TargetType; domain: string | null;
  riskScore: number; confidence: number; okCollectors: CollectorRecord[];
}): Entity[] {
  const primaryLabel = labelForTarget(a.target, a.targetType);
  const out: Entity[] = [];
  const seen = new Set<string>();
  const push = (e: Entity) => {
    const key = `${e.type}::${e.label.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(e);
  };

  push({ id: "scan-target", type: entityTypeForTarget(a.targetType), label: a.target, sub: `Input classified as ${a.targetType}`, confidence: 100, risk: Math.max(15, a.riskScore - 10) });
  if (primaryLabel && primaryLabel !== a.target) {
    push({ id: "scan-subject", type: entityTypeForTarget(a.targetType), label: primaryLabel, sub: "Resolved primary subject", confidence: a.confidence, risk: a.riskScore });
  }
  if (a.domain) {
    push({ id: "scan-domain", type: "domain", label: a.domain, sub: "Derived pivot domain", confidence: 92, risk: Math.max(10, a.riskScore - 20) });
  }

  for (const c of a.okCollectors) {
    const data = (c.data as any) ?? {};
    const s = c.source;
    if (s === "GitHub profile" && data.login) {
      push({ id: `gh-${data.login}`, type: "username", label: data.login, sub: `GitHub · ${data.public_repos ?? 0} repos · ${data.followers ?? 0} followers`, confidence: 90, risk: 25 });
    } else if (s === "OpenSanctions screening" && Array.isArray(data.results)) {
      for (const r of data.results.slice(0, 5)) {
        if (r?.caption) push({ id: `os-${r.id ?? r.caption}`.slice(0, 60), type: "person", label: String(r.caption), sub: `Sanctions/PEP · ${r.schema ?? ""}`, confidence: 92, risk: 90 });
      }
    } else if (s === "OpenCorporates" && Array.isArray(data.items)) {
      for (const r of data.items.slice(0, 5)) {
        const name = r?.company?.name;
        if (name) push({ id: `oc-${name}`.slice(0, 60), type: "org", label: String(name), sub: `OpenCorporates · ${r?.company?.jurisdiction_code ?? ""}`, confidence: 88, risk: 35 });
      }
    } else if (s === "GLEIF (LEI)" && Array.isArray(data.entities)) {
      for (const r of data.entities.slice(0, 5)) {
        const name = r?.attributes?.entity?.legalName?.name;
        if (name) push({ id: `lei-${name}`.slice(0, 60), type: "org", label: String(name), sub: `LEI · ${r?.id ?? ""}`, confidence: 94, risk: 30 });
      }
    } else if (s === "Ethplorer wallet" && data.eth) {
      push({ id: `wallet-${a.target}`.slice(0, 60), type: "wallet", label: a.target, sub: `${data.eth.balance ?? 0} ETH · ${data.txs ?? 0} txs`, confidence: 96, risk: 40 });
    } else if (s === "IP geolocation" && data.country) {
      push({ id: `geo-${data.city ?? data.country}`.slice(0, 60), type: "location", label: `${data.city ?? "?"}, ${data.country}`, sub: data.isp ?? data.org ?? "", confidence: 85, risk: 20 });
    }
  }

  return out.slice(0, 32);
}

function buildTimeline(a: {
  target: string; createdAt: string; completedAt: string;
  collectors: CollectorRecord[]; riskScore: number | null; confidence: number | null;
}): TimelineEvent[] {
  const startedMs = Date.parse(a.createdAt) || Date.now();
  const firstDone = a.collectors.reduce((m, c) => Math.min(m, c.latencyMs || Infinity), Infinity);
  const firstDoneTs = Number.isFinite(firstDone) ? new Date(startedMs + firstDone).toISOString() : a.createdAt;
  const errorCount = a.collectors.filter((c) => c.status === "error").length;
  const risk = a.riskScore;
  const scoreLine =
    risk === null
      ? "No trust score calculated — insufficient evidence"
      : `Trust ${100 - risk}/100 (risk ${risk}/100) · confidence ${a.confidence === null ? "not calculated" : `${a.confidence}%`}`;
  const runEvents: TimelineEvent[] = [
    { id: "bt-1", ts: a.createdAt, title: `Scan queued for ${a.target}`, source: "CARIO backend", severity: "low", category: "run", kind: "run", precision: "day" },
    { id: "bt-2", ts: firstDoneTs, title: `First collector responded`, source: "Collector orchestrator", severity: "low", category: "run", kind: "run", precision: "day" },
    { id: "bt-3", ts: a.completedAt, title: `${a.collectors.length} collectors completed (${errorCount} errored)`, source: "Collector orchestrator", severity: errorCount ? "medium" : "low", category: "run", kind: "run", precision: "day" },
    { id: "bt-4", ts: a.completedAt, title: scoreLine, source: "Scoring engine", severity: risk !== null && risk > 70 ? "high" : risk !== null && risk > 40 ? "medium" : "low", category: "run", kind: "run", precision: "day" },
  ];


  const evidence = reconstructEvidenceTimeline(a.collectors);
  // Oldest first: investigators need "what happened first" to read top-down.
  return [...evidence, ...runEvents].sort((x, y) => x.ts.localeCompare(y.ts));
}

// ============================================================
// Timeline reconstruction — mines dated evidence out of every
// collector payload. Undated evidence is skipped rather than
// stamped with "now", which is what made the old timeline lie.
// ============================================================

const TIMELINE_DATE_KEY =
  /(^|_|\.)((event)?date|datetime|created(_?utc|_?at)?|registered|registration|updated_?at|changed_?at|expir\w*|published(_?at)?|pubdate|seendate|first_?seen|last_?seen|incorporation_?date|dissolution_?date|retrieved_?at|timestamp|time|filed|filing_?date|date_?filed|start_?date|end_?date|snapshot)$/i;

const TIMELINE_LABEL_KEY = /^(name|title|company_?name|legal_?name|label|caption|action|eventaction|url|link|case_?name|subject|headline)$/i;

function timelineCategoryFor(source: string): TimelineCategory {
  const s = source.toLowerCase();
  if (/pwned|breach|leak/.test(s)) return "breach";
  if (/court|listener|govinfo|docket|sanction|ofac|sdn|wanted|hmt/.test(s)) return "legal";
  if (/opencorporates|gleif|lei|registry|companies|business/.test(s)) return "corporate";
  if (/cert|crt\.sh|certspotter|transparency/.test(s)) return "certificate";
  if (/rdap|whois|dns|domain/.test(s)) return "domain";
  if (/wayback|archive|common crawl/.test(s)) return "archive";
  if (/gdelt|news|hacker news|rss/.test(s)) return "news";
  if (/arxiv|openalex|document|pdf|filing|paper/.test(s)) return "document";
  if (/github|reddit|gravatar|twitter|mastodon|profile|keyserver|social|user/.test(s)) return "social";
  if (/ether|wallet|blockchain|etherscan|bitcoin|btc/.test(s)) return "blockchain";
  if (/urlhaus|phish|otx|threat|tor|urlscan/.test(s)) return "threat";
  return "document";
}

function timelineSeverityFor(category: TimelineCategory): Finding["severity"] {
  if (category === "breach") return "high";
  if (category === "threat" || category === "legal") return "medium";
  return "low";
}

type ParsedDate = { iso: string; precision: NonNullable<TimelineEvent["precision"]> };

function parseEvidenceDate(value: unknown): ParsedDate | null {
  const nowMs = Date.now();
  const floorMs = Date.parse("1975-01-01T00:00:00.000Z");
  const ceilMs = nowMs + 1000 * 60 * 60 * 24 * 365 * 30; // allow future expiry dates

  const ok = (ms: number, precision: ParsedDate["precision"]): ParsedDate | null => {
    if (!Number.isFinite(ms) || ms < floorMs || ms > ceilMs) return null;
    return { iso: new Date(ms).toISOString(), precision };
  };

  if (typeof value === "number") {
    // epoch seconds vs milliseconds
    if (value > 1e12) return ok(value, "day");
    if (value > 1e9) return ok(value * 1000, "day");
    return null;
  }
  if (typeof value !== "string") return null;
  const v = value.trim();
  if (!v) return null;

  // Pure epoch in a string
  if (/^\d{10}$/.test(v)) return ok(Number(v) * 1000, "day");
  if (/^\d{13}$/.test(v)) return ok(Number(v), "day");
  // Wayback / GDELT compact stamps: 20240102120000 or 20240102T120000Z
  const compact = v.match(/^(\d{4})(\d{2})(\d{2})(?:T?(\d{2})(\d{2})(\d{2})Z?)?$/);
  if (compact) {
    const [, y, mo, d, h = "00", mi = "00", se = "00"] = compact;
    return ok(Date.parse(`${y}-${mo}-${d}T${h}:${mi}:${se}Z`), "day");
  }
  // ISO-ish
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return ok(Date.parse(v.length === 10 ? `${v}T00:00:00Z` : v), "day");
  if (/^\d{4}-\d{2}$/.test(v)) return ok(Date.parse(`${v}-01T00:00:00Z`), "month");
  if (/^\d{4}$/.test(v)) {
    const y = Number(v);
    if (y < 1975 || y > new Date().getUTCFullYear() + 1) return null;
    return ok(Date.parse(`${v}-01-01T00:00:00Z`), "year");
  }
  const parsed = Date.parse(v);
  if (Number.isFinite(parsed)) return ok(parsed, "approx");
  return null;
}

function humanizeDateKey(key: string): string {
  const k = key.replace(/[_.]/g, " ").trim().toLowerCase();
  const map: Record<string, string> = {
    "created at": "Created",
    "created utc": "Created",
    created: "Created",
    "registration date": "Registered",
    registration: "Registered",
    registered: "Registered",
    "updated at": "Updated",
    "incorporation date": "Incorporated",
    "dissolution date": "Dissolved",
    "date filed": "Filed",
    "filing date": "Filed",
    filed: "Filed",
    "published at": "Published",
    published: "Published",
    pubdate: "Published",
    seendate: "Reported",
    "first seen": "First seen",
    "last seen": "Last seen",
    expiration: "Expires",
    "expiration date": "Expires",
    expires: "Expires",
    timestamp: "Recorded",
    time: "Recorded",
    date: "Dated",
    snapshot: "Archived",
  };
  if (map[k]) return map[k];
  if (/expir/.test(k)) return "Expires";
  return k.charAt(0).toUpperCase() + k.slice(1);
}

function nearestLabel(obj: Record<string, unknown>): { label?: string; ref?: string } {
  let label: string | undefined;
  let ref: string | undefined;
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v !== "string" || !v.trim()) continue;
    if (/^(url|link|html_url)$/i.test(k) && /^https?:\/\//.test(v)) { ref ??= v; continue; }
    if (TIMELINE_LABEL_KEY.test(k) && !label) label = v.slice(0, 140);
  }
  return { label, ref };
}

/** Recursively walk a collector payload and lift every dated fact out of it. */
function extractDatedFacts(
  node: unknown,
  depth: number,
  inheritedLabel: string | undefined,
  inheritedRef: string | undefined,
  out: Array<{ key: string; date: ParsedDate; label?: string; ref?: string }>,
): void {
  if (depth > 4 || out.length > 60 || node == null) return;
  if (Array.isArray(node)) {
    for (const item of node.slice(0, 12)) extractDatedFacts(item, depth + 1, inheritedLabel, inheritedRef, out);
    return;
  }
  if (typeof node !== "object") return;
  const obj = node as Record<string, unknown>;
  const local = nearestLabel(obj);
  const label = local.label ?? inheritedLabel;
  const ref = local.ref ?? inheritedRef;

  // RDAP-style { eventAction, eventDate } pairs get an explicit action label.
  const eventAction = typeof obj["eventAction"] === "string" ? String(obj["eventAction"]) : undefined;

  for (const [k, v] of Object.entries(obj)) {
    if (v && typeof v === "object") {
      extractDatedFacts(v, depth + 1, label, ref, out);
      continue;
    }
    if (!TIMELINE_DATE_KEY.test(k)) continue;
    const date = parseEvidenceDate(v);
    if (!date) continue;
    out.push({ key: eventAction ?? k, date, label, ref });
  }
}

function reconstructEvidenceTimeline(collectors: CollectorRecord[]): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  const seen = new Set<string>();

  for (const c of collectors) {
    if (c.status === "error" || !c.data) continue;
    const facts: Array<{ key: string; date: ParsedDate; label?: string; ref?: string }> = [];
    extractDatedFacts(c.data, 0, undefined, undefined, facts);
    if (!facts.length) continue;

    const category = timelineCategoryFor(c.source);
    const severity = timelineSeverityFor(category);

    for (const f of facts.slice(0, 14)) {
      const action = humanizeDateKey(f.key);
      const title = f.label ? `${action}: ${f.label}` : `${action} · ${c.source}`;
      const dedupe = `${category}|${f.date.iso.slice(0, 10)}|${title.toLowerCase()}`;
      if (seen.has(dedupe)) continue;
      seen.add(dedupe);
      events.push({
        id: `tl-${category}-${events.length}-${f.date.iso.slice(0, 10)}`,
        ts: f.date.iso,
        title: title.slice(0, 180),
        source: c.source,
        severity,
        category,
        precision: f.date.precision,
        kind: "evidence",
        ...(f.ref ? { ref: f.ref } : {}),
      });
    }
  }

  return events.sort((a, b) => a.ts.localeCompare(b.ts)).slice(0, 160);
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function entityTypeForTarget(targetType: TargetType): Entity["type"] {
  if (targetType === "organization") return "org";
  if (targetType === "domain") return "domain";
  if (targetType === "ip") return "ip";
  if (targetType === "wallet") return "wallet";
  if (targetType === "username") return "username";
  if (targetType === "phone") return "phone";
  if (targetType === "email") return "email";
  return "person";
}

function labelForTarget(target: string, targetType: TargetType) {
  if (targetType === "email") {
    const local = target.split("@")[0] ?? target;
    return local.split(/[._-]/).filter(Boolean).map((p) => p[0]?.toUpperCase() + p.slice(1)).join(" ") || target;
  }
  if (targetType === "username") return target.startsWith("@") ? target : `@${target}`;
  return target;
}

// ============================================================
// Extended collectors (timeout-protected, all-settled)
// ============================================================

async function fetchWaybackCdxCount(domain: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const r = await fetchWithTimeout(`https://web.archive.org/cdx/search/cdx?url=${encodeURIComponent(domain)}&output=json&limit=500&fl=timestamp,original&filter=statuscode:200`);
    if (!r.ok) return { source: "Wayback CDX", status: "miss", summary: `CDX returned ${r.status}`, latencyMs: Date.now() - started };
    const rows = (await r.json()) as any[];
    const count = Math.max(0, (rows?.length ?? 1) - 1);
    return { source: "Wayback CDX", status: count ? "ok" : "miss", summary: `${count} archived snapshots indexed`, latencyMs: Date.now() - started, data: sanitizeJson({ count, sample: rows?.slice(1, 6) }) };
  } catch (e) { return { source: "Wayback CDX", status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

async function fetchUrlscan(domain: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const r = await fetchWithTimeout(`https://urlscan.io/api/v1/search/?q=domain:${encodeURIComponent(domain)}&size=5`);
    if (!r.ok) return { source: "urlscan.io", status: "miss", summary: `urlscan returned ${r.status}`, latencyMs: Date.now() - started };
    const data = await r.json() as { total?: number; results?: any[] };
    const total = data.total ?? 0;
    return { source: "urlscan.io", status: total > 0 ? "ok" : "miss", summary: `${total} public urlscan submissions`, latencyMs: Date.now() - started, data: sanitizeJson({ total, results: (data.results ?? []).slice(0, 5).map((x) => ({ url: x.task?.url, time: x.task?.time, screenshot: x.screenshot })) }) };
  } catch (e) { return { source: "urlscan.io", status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

async function fetchMozillaObservatory(domain: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const r = await fetchWithTimeout(`https://http-observatory.security.mozilla.org/api/v1/analyze?host=${encodeURIComponent(domain)}`, { method: "POST" });
    if (!r.ok) return { source: "Mozilla Observatory", status: "miss", summary: `Observatory returned ${r.status}`, latencyMs: Date.now() - started };
    const data = await r.json() as any;
    return { source: "Mozilla Observatory", status: data?.grade ? "ok" : "miss", summary: `Grade ${data?.grade ?? "?"} · score ${data?.score ?? "?"} · state ${data?.state ?? "?"}`, latencyMs: Date.now() - started, data: sanitizeJson(data) };
  } catch (e) { return { source: "Mozilla Observatory", status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

async function fetchHackerTarget(target: string, op: string): Promise<CollectorRecord> {
  const started = Date.now();
  const label = `HackerTarget ${op}`;
  try {
    const r = await fetchWithTimeout(`https://api.hackertarget.com/${op}/?q=${encodeURIComponent(target)}`);
    const text = (await r.text()).slice(0, 8000);
    if (!r.ok || /error|API count exceeded/i.test(text)) return { source: label, status: "miss", summary: text.slice(0, 120) || `${op} returned ${r.status}`, latencyMs: Date.now() - started };
    const lines = text.split(/\r?\n/).filter(Boolean);
    return { source: label, status: lines.length ? "ok" : "miss", summary: `${lines.length} rows from public ${op}`, latencyMs: Date.now() - started, data: sanitizeJson({ preview: lines.slice(0, 20) }) };
  } catch (e) { return { source: label, status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

async function fetchGdelt(q: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const r = await fetchWithTimeout(`https://api.gdeltproject.org/api/v2/doc/doc?query=${encodeURIComponent(q)}&format=json&maxrecords=10&sort=DateDesc`);
    if (!r.ok) return { source: "GDELT news", status: "miss", summary: `GDELT returned ${r.status}`, latencyMs: Date.now() - started };
    const text = await r.text();
    let data: any = {}; try { data = JSON.parse(text); } catch { /* gdelt sometimes returns html */ }
    const articles = (data.articles ?? []) as any[];
    return { source: "GDELT news", status: articles.length ? "ok" : "miss", summary: articles.length ? `${articles.length} recent news articles · top: ${articles[0]?.title?.slice(0, 80) ?? "—"}` : "No GDELT articles", latencyMs: Date.now() - started, data: sanitizeJson({ articles: articles.slice(0, 6).map((a) => ({ title: a.title, url: a.url, seendate: a.seendate, domain: a.domain })) }) };
  } catch (e) { return { source: "GDELT news", status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

async function fetchGdeltGeneric(q: string): Promise<CollectorRecord> {
  return fetchGdelt(q);
}

async function fetchOpenCorporates(q: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const r = await fetchWithTimeout(`https://api.opencorporates.com/v0.4/companies/search?q=${encodeURIComponent(q)}&format=json&per_page=5`);
    if (!r.ok) return { source: "OpenCorporates", status: "miss", summary: `OpenCorporates returned ${r.status}`, latencyMs: Date.now() - started };
    const data = await r.json() as any;
    const cos = data?.results?.companies ?? [];
    return { source: "OpenCorporates", status: cos.length ? "ok" : "miss", summary: cos.length ? `${cos.length} corporate registrations · top: ${cos[0]?.company?.name}` : "No company registrations found", latencyMs: Date.now() - started, data: sanitizeJson({ companies: cos.slice(0, 5).map((c: any) => c.company) }) };
  } catch (e) { return { source: "OpenCorporates", status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

async function fetchIpWhoIs(ip: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const r = await fetchWithTimeout(`https://ipwho.is/${encodeURIComponent(ip)}`);
    if (!r.ok) return { source: "ipwho.is", status: "miss", summary: `ipwho returned ${r.status}`, latencyMs: Date.now() - started };
    const data = await r.json() as any;
    if (!data?.success) return { source: "ipwho.is", status: "miss", summary: data?.message || "ipwho.is no result", latencyMs: Date.now() - started };
    return { source: "ipwho.is", status: "ok", summary: `${data.city}, ${data.country} · ${data.connection?.isp ?? "?"} · AS${data.connection?.asn ?? "?"}`, latencyMs: Date.now() - started, data: sanitizeJson(data) };
  } catch (e) { return { source: "ipwho.is", status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

async function fetchShodanInternetDB(ip: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const r = await fetchWithTimeout(`https://internetdb.shodan.io/${encodeURIComponent(ip)}`);
    if (r.status === 404) return { source: "Shodan InternetDB", status: "miss", summary: "No public Shodan record for this IP", latencyMs: Date.now() - started };
    if (!r.ok) return { source: "Shodan InternetDB", status: "miss", summary: `Shodan returned ${r.status}`, latencyMs: Date.now() - started };
    const data = await r.json() as any;
    return { source: "Shodan InternetDB", status: "ok", summary: `${(data.ports ?? []).length} open port(s) · ${(data.vulns ?? []).length} CVE flags · ${(data.hostnames ?? []).length} hostname(s)`, latencyMs: Date.now() - started, data: sanitizeJson(data) };
  } catch (e) { return { source: "Shodan InternetDB", status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

async function fetchGreyNoise(ip: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const r = await fetchWithTimeout(`https://api.greynoise.io/v3/community/${encodeURIComponent(ip)}`);
    if (!r.ok && r.status !== 404) return { source: "GreyNoise community", status: "miss", summary: `GreyNoise returned ${r.status}`, latencyMs: Date.now() - started };
    const data = await r.json() as any;
    return { source: "GreyNoise community", status: "ok", summary: `${data.classification ?? "unknown"} · noise=${data.noise} · riot=${data.riot}`, latencyMs: Date.now() - started, data: sanitizeJson(data) };
  } catch (e) { return { source: "GreyNoise community", status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

async function fetchGitlabUser(username: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const r = await fetchWithTimeout(`https://gitlab.com/api/v4/users?username=${encodeURIComponent(username)}`);
    if (!r.ok) return { source: "GitLab profile", status: "miss", summary: `GitLab returned ${r.status}`, latencyMs: Date.now() - started };
    const arr = await r.json() as any[];
    if (!arr?.length) return { source: "GitLab profile", status: "miss", summary: `No GitLab user ${username}`, latencyMs: Date.now() - started };
    return { source: "GitLab profile", status: "ok", summary: `${arr[0].username} · ${arr[0].name ?? "—"} · since ${String(arr[0].created_at).slice(0, 10)}`, latencyMs: Date.now() - started, data: sanitizeJson(arr[0]) };
  } catch (e) { return { source: "GitLab profile", status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

async function fetchBitbucketUser(username: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const r = await fetchWithTimeout(`https://api.bitbucket.org/2.0/users/${encodeURIComponent(username)}`);
    if (!r.ok) return { source: "Bitbucket profile", status: "miss", summary: `Bitbucket returned ${r.status}`, latencyMs: Date.now() - started };
    const data = await r.json() as any;
    return { source: "Bitbucket profile", status: "ok", summary: `${data.display_name ?? username} · created ${String(data.created_on ?? "").slice(0, 10)}`, latencyMs: Date.now() - started, data: sanitizeJson(data) };
  } catch (e) { return { source: "Bitbucket profile", status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

async function fetchKeybaseUser(username: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const r = await fetchWithTimeout(`https://keybase.io/_/api/1.0/user/lookup.json?usernames=${encodeURIComponent(username)}`);
    if (!r.ok) return { source: "Keybase", status: "miss", summary: `Keybase returned ${r.status}`, latencyMs: Date.now() - started };
    const data = await r.json() as any;
    const them = (data.them ?? [])[0];
    if (!them) return { source: "Keybase", status: "miss", summary: `No Keybase user ${username}`, latencyMs: Date.now() - started };
    return { source: "Keybase", status: "ok", summary: `${them.basics?.username} · ${Object.keys(them.proofs_summary?.by_proof_type ?? {}).length} linked proofs`, latencyMs: Date.now() - started, data: sanitizeJson({ basics: them.basics, proofs: them.proofs_summary }) };
  } catch (e) { return { source: "Keybase", status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

async function fetchDevToUser(username: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const r = await fetchWithTimeout(`https://dev.to/api/users/by_username?url=${encodeURIComponent(username)}`);
    if (!r.ok) return { source: "DEV.to profile", status: "miss", summary: `DEV.to returned ${r.status}`, latencyMs: Date.now() - started };
    const data = await r.json() as any;
    return { source: "DEV.to profile", status: "ok", summary: `@${data.username} · ${data.name ?? "—"} · joined ${String(data.joined_at ?? "")}`, latencyMs: Date.now() - started, data: sanitizeJson(data) };
  } catch (e) { return { source: "DEV.to profile", status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

async function fetchStackExchangeUser(username: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const r = await fetchWithTimeout(`https://api.stackexchange.com/2.3/users?inname=${encodeURIComponent(username)}&site=stackoverflow&pagesize=5`);
    if (!r.ok) return { source: "StackExchange", status: "miss", summary: `StackExchange returned ${r.status}`, latencyMs: Date.now() - started };
    const data = await r.json() as any;
    const items = data?.items ?? [];
    return { source: "StackExchange", status: items.length ? "ok" : "miss", summary: items.length ? `${items.length} SO matches · top: ${items[0].display_name} (${items[0].reputation} rep)` : "No StackExchange match", latencyMs: Date.now() - started, data: sanitizeJson({ items: items.slice(0, 5) }) };
  } catch (e) { return { source: "StackExchange", status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

async function fetchMastodonSearch(q: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const r = await fetchWithTimeout(`https://mastodon.social/api/v2/search?q=${encodeURIComponent(q)}&type=accounts&limit=5&resolve=false`);
    if (!r.ok) return { source: "Mastodon", status: "miss", summary: `Mastodon returned ${r.status}`, latencyMs: Date.now() - started };
    const data = await r.json() as any;
    const acc = data?.accounts ?? [];
    return { source: "Mastodon", status: acc.length ? "ok" : "miss", summary: acc.length ? `${acc.length} fediverse accounts · top: @${acc[0].acct}` : "No fediverse account", latencyMs: Date.now() - started, data: sanitizeJson({ accounts: acc.slice(0, 5).map((a: any) => ({ acct: a.acct, url: a.url, followers: a.followers_count })) }) };
  } catch (e) { return { source: "Mastodon", status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

const SHERLOCK_SITES: Array<{ name: string; url: (u: string) => string; missCodes?: number[] }> = [
  { name: "Twitch",       url: (u) => `https://www.twitch.tv/${u}` },
  { name: "YouTube @",    url: (u) => `https://www.youtube.com/@${u}` },
  { name: "Instagram",    url: (u) => `https://www.instagram.com/${u}/` },
  { name: "TikTok",       url: (u) => `https://www.tiktok.com/@${u}` },
  { name: "Pinterest",    url: (u) => `https://www.pinterest.com/${u}/` },
  { name: "Medium",       url: (u) => `https://medium.com/@${u}` },
  { name: "Spotify",      url: (u) => `https://open.spotify.com/user/${u}` },
  { name: "SoundCloud",   url: (u) => `https://soundcloud.com/${u}` },
  { name: "Steam",        url: (u) => `https://steamcommunity.com/id/${u}` },
  { name: "Vimeo",        url: (u) => `https://vimeo.com/${u}` },
  { name: "Patreon",      url: (u) => `https://www.patreon.com/${u}` },
  { name: "Behance",      url: (u) => `https://www.behance.net/${u}` },
  { name: "Dribbble",     url: (u) => `https://dribbble.com/${u}` },
  { name: "Flickr",       url: (u) => `https://www.flickr.com/people/${u}` },
  { name: "Lobsters",     url: (u) => `https://lobste.rs/u/${u}` },
  { name: "ProductHunt",  url: (u) => `https://www.producthunt.com/@${u}` },
  { name: "Telegram",     url: (u) => `https://t.me/${u}` },
  { name: "Twitter",      url: (u) => `https://twitter.com/${u}` },
  { name: "Threads",      url: (u) => `https://www.threads.net/@${u}` },
  { name: "Bluesky",      url: (u) => `https://bsky.app/profile/${u}.bsky.social` },
  { name: "GitHub",       url: (u) => `https://github.com/${u}` },
  { name: "Replit",       url: (u) => `https://replit.com/@${u}` },
  { name: "Codepen",      url: (u) => `https://codepen.io/${u}` },
];

function sherlockProbe(username: string): Promise<CollectorRecord>[] {
  return SHERLOCK_SITES.map(async (site): Promise<CollectorRecord> => {
    const started = Date.now();
    const url = site.url(username);
    try {
      const r = await fetchWithTimeout(url, { method: "GET", redirect: "manual", timeout: 3500 });
      const found = r.status === 200;
      return { source: `Username: ${site.name}`, status: found ? "ok" : "miss", summary: found ? `Account exists at ${url}` : `No account (HTTP ${r.status})`, latencyMs: Date.now() - started, data: sanitizeJson({ url, status: r.status }) };
    } catch (e) {
      return { source: `Username: ${site.name}`, status: "error", summary: errMsg(e), latencyMs: Date.now() - started, data: sanitizeJson({ url }) };
    }
  });
}

async function fetchXposedOrNot(email: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const r = await fetchWithTimeout(`https://api.xposedornot.com/v1/breach-analytics?email=${encodeURIComponent(email)}`);
    if (r.status === 404) return { source: "XposedOrNot breaches", status: "miss", summary: "No public breaches surfaced for this email", latencyMs: Date.now() - started };
    if (!r.ok) return { source: "XposedOrNot breaches", status: "miss", summary: `XposedOrNot returned ${r.status}`, latencyMs: Date.now() - started };
    const data = await r.json() as any;
    const list = data?.ExposedBreaches?.breaches_details ?? data?.BreachMetrics ?? [];
    const count = Array.isArray(list) ? list.length : Object.keys(list).length;
    return { source: "XposedOrNot breaches", status: count ? "ok" : "miss", summary: count ? `${count} breach record group(s) discovered` : "No exposure found", latencyMs: Date.now() - started, data: sanitizeJson(data) };
  } catch (e) { return { source: "XposedOrNot breaches", status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

const DISPOSABLE_HINTS = ["mailinator", "guerrillamail", "10minutemail", "tempmail", "yopmail", "throwaway", "trashmail", "discard", "fakeinbox", "getnada", "moakt", "sharklasers"];

async function fetchDisposableEmail(email: string): Promise<CollectorRecord> {
  const started = Date.now();
  const dom = email.split("@")[1]?.toLowerCase() ?? "";
  const disposable = DISPOSABLE_HINTS.some((h) => dom.includes(h));
  return { source: "Disposable-email heuristic", status: disposable ? "ok" : "miss", summary: disposable ? `Domain ${dom} matches a known disposable provider pattern` : `Domain ${dom} not on local disposable list`, latencyMs: Date.now() - started, data: sanitizeJson({ domain: dom, disposable }) };
}

async function fetchEnsReverse(address: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const r = await fetchWithTimeout(`https://api.ensideas.com/ens/resolve/${encodeURIComponent(address)}`);
    if (!r.ok) return { source: "ENS reverse", status: "miss", summary: `ENS returned ${r.status}`, latencyMs: Date.now() - started };
    const data = await r.json() as any;
    return { source: "ENS reverse", status: data?.name ? "ok" : "miss", summary: data?.name ? `Reverse ENS: ${data.name}` : "No ENS reverse record", latencyMs: Date.now() - started, data: sanitizeJson(data) };
  } catch (e) { return { source: "ENS reverse", status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

async function fetchBlockchairAddress(chain: "ethereum" | "bitcoin", address: string): Promise<CollectorRecord> {
  const started = Date.now();
  const label = `Blockchair ${chain}`;
  try {
    const r = await fetchWithTimeout(`https://api.blockchair.com/${chain}/dashboards/address/${encodeURIComponent(address)}?limit=0`);
    if (!r.ok) return { source: label, status: "miss", summary: `Blockchair returned ${r.status}`, latencyMs: Date.now() - started };
    const data = await r.json() as any;
    const addr = data?.data?.[address]?.address;
    if (!addr) return { source: label, status: "miss", summary: "Blockchair address not indexed", latencyMs: Date.now() - started };
    return { source: label, status: "ok", summary: `${addr.transaction_count ?? 0} txs · balance ${addr.balance ?? "?"} · first seen ${addr.first_seen_receiving ?? "?"}`, latencyMs: Date.now() - started, data: sanitizeJson(addr) };
  } catch (e) { return { source: label, status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

async function fetchBlockstreamBtc(address: string): Promise<CollectorRecord> {
  const started = Date.now();
  try {
    const r = await fetchWithTimeout(`https://blockstream.info/api/address/${encodeURIComponent(address)}`);
    if (!r.ok) return { source: "Blockstream BTC", status: "miss", summary: `Blockstream returned ${r.status}`, latencyMs: Date.now() - started };
    const data = await r.json() as any;
    return { source: "Blockstream BTC", status: "ok", summary: `${data.chain_stats?.tx_count ?? 0} txs · funded ${data.chain_stats?.funded_txo_sum ?? 0} sats · spent ${data.chain_stats?.spent_txo_sum ?? 0} sats`, latencyMs: Date.now() - started, data: sanitizeJson(data) };
  } catch (e) { return { source: "Blockstream BTC", status: "error", summary: errMsg(e), latencyMs: Date.now() - started }; }
}

const PHONE_COUNTRY: Record<string, string> = {
  "1": "US/Canada", "7": "Russia/Kazakhstan", "20": "Egypt", "27": "South Africa", "30": "Greece", "31": "Netherlands",
  "32": "Belgium", "33": "France", "34": "Spain", "36": "Hungary", "39": "Italy", "40": "Romania", "41": "Switzerland",
  "43": "Austria", "44": "United Kingdom", "45": "Denmark", "46": "Sweden", "47": "Norway", "48": "Poland", "49": "Germany",
  "51": "Peru", "52": "Mexico", "53": "Cuba", "54": "Argentina", "55": "Brazil", "56": "Chile", "57": "Colombia",
  "58": "Venezuela", "60": "Malaysia", "61": "Australia", "62": "Indonesia", "63": "Philippines", "64": "New Zealand",
  "65": "Singapore", "66": "Thailand", "81": "Japan", "82": "South Korea", "84": "Vietnam", "86": "China",
  "90": "Turkey", "91": "India", "92": "Pakistan", "93": "Afghanistan", "94": "Sri Lanka", "95": "Myanmar",
  "98": "Iran", "212": "Morocco", "213": "Algeria", "216": "Tunisia", "218": "Libya", "220": "Gambia",
  "234": "Nigeria", "254": "Kenya", "256": "Uganda", "263": "Zimbabwe", "351": "Portugal", "352": "Luxembourg",
  "353": "Ireland", "354": "Iceland", "358": "Finland", "359": "Bulgaria", "370": "Lithuania", "371": "Latvia",
  "372": "Estonia", "380": "Ukraine", "385": "Croatia", "386": "Slovenia", "420": "Czech Republic", "421": "Slovakia",
  "966": "Saudi Arabia", "971": "UAE", "972": "Israel", "974": "Qatar", "880": "Bangladesh", "852": "Hong Kong",
  "853": "Macau", "886": "Taiwan",
};

async function fetchPhoneClassify(phone: string): Promise<CollectorRecord> {
  const started = Date.now();
  const digits = phone.replace(/[^0-9]/g, "");
  let country = "Unknown";
  for (const len of [3, 2, 1]) {
    const cc = digits.slice(0, len);
    if (PHONE_COUNTRY[cc]) { country = `+${cc} ${PHONE_COUNTRY[cc]}`; break; }
  }
  return { source: "Phone classification", status: country === "Unknown" ? "miss" : "ok", summary: `Country mapping: ${country} · ${digits.length} digits`, latencyMs: Date.now() - started, data: sanitizeJson({ digits, country }) };
}

async function fetchPivotLinks(target: string): Promise<CollectorRecord> {
  const started = Date.now();
  const q = encodeURIComponent(target);
  const links = {
    google: `https://www.google.com/search?q=${q}`,
    bing: `https://www.bing.com/search?q=${q}`,
    duckduckgo: `https://duckduckgo.com/?q=${q}`,
    yandex: `https://yandex.com/search/?text=${q}`,
    archiveOrg: `https://web.archive.org/web/*/${q}`,
    archiveToday: `https://archive.ph/${target}`,
    googleDorkPdf: `https://www.google.com/search?q=${q}+filetype%3Apdf`,
    googleDorkSite: `https://www.google.com/search?q=site%3A${q}`,
    haveIBeenTrained: `https://haveibeentrained.com/?search_text=${q}`,
    intelx: `https://intelx.io/?s=${q}`,
    shodanWeb: `https://www.shodan.io/search?query=${q}`,
    censys: `https://search.censys.io/search?resource=hosts&q=${q}`,
    fofa: `https://en.fofa.info/result?qbase64=${btoa(target).replace(/=+$/, "")}`,
  };
  return { source: "Investigator pivot links", status: "ok", summary: `${Object.keys(links).length} ready-to-open public search pivots`, latencyMs: Date.now() - started, data: sanitizeJson(links) };
}

function errMsg(e: unknown): string {
  if (e instanceof Error) return e.message;
  return "fetch failed";
}

/**
 * Identity block handed to Grok before the raw corpus: the corroborated social
 * profiles and contact facts are what a person search is judged on, so the
 * synthesis layer must reason about them explicitly instead of hoping to spot
 * them inside 24k characters of collector JSON.
 */
function identityDigest(collectors: CollectorRecord[]): string {
  const ok = collectors.filter((c) => c.status === "ok");
  const social = buildSocialLane(ok);
  const cg = ok.find((c) => c.source === "Contact graph")?.data as any;
  const lines: string[] = ["RESOLVED IDENTITY SURFACE (deterministic, pre-computed):"];

  if (social.length) {
    lines.push("Social profiles (verdict · corroborating sources · url):");
    for (const p of social.slice(0, 30)) {
      lines.push(`- ${p.platform} ${p.handle ?? ""} — ${p.verdict} · ${p.corroboration} probe(s) · ${p.sources.slice(0, 3).join(", ")} · ${p.url}`);
    }
  } else {
    lines.push("Social profiles: none resolved deterministically. Treat this as a gap to close with live search, NOT as proof the target has no accounts.");
  }

  const bag = (label: string, items: any[] | undefined) => {
    if (!Array.isArray(items) || !items.length) return;
    lines.push(`${label}:`);
    for (const it of items.slice(0, 20)) {
      lines.push(`- ${it.value} (corroboration ${it.corroboration ?? 1}${it.sources?.length ? `, sources: ${it.sources.slice(0, 3).join(", ")}` : ""})`);
    }
  };
  if (cg) {
    bag("Emails", cg.emails);
    bag("Phones", cg.phones);
    bag("Addresses", cg.addresses);
    bag("Possible associates", cg.relatives);
  }

  lines.push(
    "RULES: a value with corroboration 1 is a CANDIDATE, not a fact — say so. Verify candidates with live search before promoting them. Never invent a profile URL that is not in this block or returned by your own search.",
  );
  return lines.join("\n");
}
